import { useState, useMemo, useEffect, useRef } from "react";
import MarketIntelligence from "./MarketIntelligence";
import { useTheme } from "./App";
import { supabase, addWantItem, readLocations } from "./supabase";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Cell, PieChart, Pie, Legend,
} from "recharts";

const fmt  = n => `$${(+n||0).toFixed(2)}`;
const fmtT = n => `$${Math.round(+n||0)}`;
const pct  = n => `${(+n||0).toFixed(1)}%`;

const REPORT_TABS = [
  { id:"pnl",     label:"P&L"             },
  { id:"health",  label:"Inventory Health" },
  { id:"sellers", label:"Best Sellers"     },
  { id:"lot",     label:"Lot ROI"          },
  { id:"buyrate", label:"Buy Rate"         },
  { id:"grading", label:"Graded Cards"     },
  { id:"consign",     label:"Consignment Sign-ups" },
  { id:"consign_inv", label:"Consignment Inventory" },
  { id:"kiosk",   label:"Kiosk Analytics"  },
  { id:"market",  label:"Market Intel"     },
  { id:"tax",     label:"Sales & Tax Summary" },
  { id:"buckets", label:"Sales by Price Range" },
];

export default function ReportsPage({ inventory, lots, shows, trades, buyers = [], onAddBuyer, isPaid = true, vendor, isStaff = false, isEnterprise = false, activeLocationId = null }) {
  const { S, T } = useTheme();
  const [activeReport, setActiveReport] = useState(isStaff ? "kiosk" : "pnl");
  const [gameFilter, setGameFilter]     = useState("all");
  const [locationFilter, setLocationFilter] = useState(activeLocationId || "all");
  const [locations, setLocations]       = useState([]);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  useEffect(() => {
    if (!isEnterprise) return;
    readLocations().then(setLocations).catch(() => {});
  }, [isEnterprise]);

  const prevActiveLocationId = useRef(activeLocationId);
  useEffect(() => {
    if (activeLocationId !== prevActiveLocationId.current) {
      setLocationFilter(activeLocationId || "all");
      prevActiveLocationId.current = activeLocationId;
    }
  }, [activeLocationId]);

  // Financial report IDs staff cannot access
  const STAFF_BLOCKED = new Set(["pnl","health","sellers","lot","buyrate","grading","consign","consign_inv","market"]);

  const VISIBLE_TABS = REPORT_TABS.filter(r => {
    if ((r.id === "consign" || r.id === "consign_inv") && !isPaid) return false;
    if (r.id === "kiosk"   && !isPaid) return false;
    if (r.id === "tax"     && !vendor?.tax_tracking_enabled) return false;
    if (r.id === "pnl"     && !isPaid) return false;
    if (r.id === "market"  && !isPaid) return false;
    if (isStaff && STAFF_BLOCKED.has(r.id)) return false;
    return true;
  });

  // Filter inventory by game, then location, before passing to all reports
  const filteredInventory = useMemo(() => {
    let result = inventory;
    if (gameFilter !== "all") result = result.filter(i => (i.GameType || "Pokemon") === gameFilter);
    if (locationFilter !== "all") result = result.filter(i => i.LocationID === locationFilter);
    return result;
  }, [inventory, gameFilter, locationFilter]);

  return (
    <div style={S.page}>
      <div style={{ marginBottom:16 }}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
          <div style={{ fontSize:16, fontWeight:700, color:T.text }}>Reports & Analytics</div>
          <div style={{display:"flex", gap:8}}>
            {/* Game filter */}
            <select style={{...S.select, width:"auto", fontSize:12, padding:"6px 10px"}}
              value={gameFilter} onChange={e=>setGameFilter(e.target.value)}>
              <option value="all">All Games</option>
              <option value="Pokemon">Pokémon</option>
              <option value="Lorcana">Lorcana</option>
              <option value="Magic">Magic</option>
              <option value="One Piece">One Piece</option>
              <option value="Yu-Gi-Oh">Yu-Gi-Oh</option>
            </select>
            {isEnterprise && locations.length > 1 && (
              <select style={{...S.select, width:"auto", fontSize:12, padding:"6px 10px"}}
                value={locationFilter} onChange={e=>setLocationFilter(e.target.value)}>
                <option value="all">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.ID} value={loc.ID}>{loc.StoreName} · #{loc.StoreNumber}</option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Report tab selector */}
        {isMobile ? (
          <select
            style={{ ...S.select, fontSize:14, fontWeight:600,
              borderColor:"#3C66EE", color:"#3C66EE" }}
            value={activeReport}
            onChange={e=>setActiveReport(e.target.value)}>
            {VISIBLE_TABS.map(r=>(
              <option key={r.id} value={r.id}>{r.label}</option>
            ))}
          </select>
        ) : (
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {VISIBLE_TABS.map(r=>(
              <button key={r.id}
                style={{ ...S.chip(activeReport===r.id), padding:"8px 14px" }}
                onClick={()=>setActiveReport(r.id)}>
                {r.label}
              </button>
            ))}
          </div>
        )}

        {/* Active game context banner */}
        {gameFilter !== "all" && (
          <div style={{marginTop:10, padding:"6px 12px", borderRadius:8, fontSize:12,
            background:"rgba(60,102,238,0.08)",
            border:"0.5px solid rgba(60,102,238,0.3)",
            color:"#3C66EE",
          }}>
            Showing {gameFilter} cards only
            {" · "}<button style={{background:"none",border:"none",cursor:"pointer",
              color:"inherit",fontSize:12,textDecoration:"underline",padding:0}}
              onClick={()=>setGameFilter("all")}>Show all</button>
          </div>
        )}
      </div>

      {!(isStaff && STAFF_BLOCKED.has(activeReport)) && <>
      {activeReport==="pnl"     && <PandL            inventory={filteredInventory} shows={shows} lots={lots} vendor={vendor} S={S} T={T} />}
      {activeReport==="tax"     && <SalesTaxSummary  inventory={filteredInventory} S={S} T={T} />}
      {activeReport==="buckets" && <SalesByPriceRange    inventory={filteredInventory} isMobile={isMobile} S={S} T={T} />}
      {activeReport==="health"  && <InventoryHealth  inventory={filteredInventory} S={S} T={T} />}
      {activeReport==="sellers" && <BestSellers      inventory={filteredInventory} S={S} T={T} />}
      {activeReport==="lot"     && <LotROI           inventory={filteredInventory} lots={lots} S={S} T={T} />}
      {activeReport==="buyrate" && <BuyRateAnalysis  inventory={filteredInventory} trades={trades} S={S} T={T} />}
      {activeReport==="grading"  && <GradedCards     inventory={filteredInventory} S={S} T={T} />}
      {activeReport==="consign"  && <ConsignmentRequests S={S} T={T} />}
      {activeReport==="consign_inv" && <ConsignmentInventory inventory={inventory} vendor={vendor} S={S} T={T} />}
      {activeReport==="market"   && <MarketIntelligence isAdmin={vendor?.role === "admin"} />}
      </>}
      {activeReport==="kiosk"    && <KioskAnalytics S={S} T={T} buyers={buyers} onAddBuyer={onAddBuyer} vendor={vendor} />}
    </div>
  );
}

// ─── INVENTORY HEALTH ─────────────────────────────────────────────────────────
function InventoryHealth({ inventory, S, T }) {
  const [showAllUnderpriced, setShowAllUnderpriced] = useState(false);
  const inStock = inventory.filter(i=>i.Status==="In Stock"||i.Status==="Reserved");
  const sold    = inventory.filter(i=>i.Status==="Sold");
  const today   = new Date();

  const daysSince = d => {
    if (!d) return 0;
    return Math.floor((today - new Date(d)) / 86400000);
  };

  // Non-movers by age bucket
  const aged30  = inStock.filter(i=>daysSince(i.AcquisitionDate)>=30  && daysSince(i.AcquisitionDate)<60);
  const aged60  = inStock.filter(i=>daysSince(i.AcquisitionDate)>=60  && daysSince(i.AcquisitionDate)<90);
  const aged90  = inStock.filter(i=>daysSince(i.AcquisitionDate)>=90);

  const deadValue = aged90.reduce((s,i)=>s+(i.AcquisitionCost||0),0);
  const totalCost = inStock.reduce((s,i)=>s+(i.AcquisitionCost||0),0);

  // Average days to sell
  const soldWithDates = sold.filter(i=>i.AcquisitionDate&&i.SoldDate);
  const avgDaysToSell = soldWithDates.length>0
    ? soldWithDates.reduce((s,i)=>s+Math.max(0,
        Math.floor((new Date(i.SoldDate)-new Date(i.AcquisitionDate))/86400000)),0) / soldWithDates.length
    : 0;

  // Underwater cards (MV < cost)
  const underwater = inStock.filter(i=>i.MarketValueAtAcquisition>0&&i.AcquisitionCost>0&&
    i.MarketValueAtAcquisition<i.AcquisitionCost);

  // Underpriced (MV > list price by 20%+)
  const underpriced = inStock.filter(i=>i.MarketValueAtAcquisition>0&&i.ListPrice>0&&
    i.MarketValueAtAcquisition>(i.ListPrice*1.2));

  // Unpriced
  const unpriced = inStock.filter(i=>!i.ListPrice||i.ListPrice<=0);

  // Age distribution chart
  const ageData = [
    {label:"0-30d",  count:inStock.filter(i=>daysSince(i.AcquisitionDate)<30).length,  fill:"#4ade80"},
    {label:"30-60d", count:aged30.length,  fill:"#60a5fa"},
    {label:"60-90d", count:aged60.length,  fill:"#fbbf24"},
    {label:"90d+",   count:aged90.length,  fill:"#f87171"},
  ];

  // Top dead stock by value
  const topDead = [...aged90].sort((a,b)=>(b.AcquisitionCost||0)-(a.AcquisitionCost||0)).slice(0,5);

  // Health score 0-100
  const deadPct    = inStock.length>0 ? aged90.length/inStock.length : 0;
  const unpricedPct= inStock.length>0 ? unpriced.length/inStock.length : 0;
  const score      = Math.max(0, Math.round(100 - (deadPct*40) - (unpricedPct*20) - (underwater.length>0?10:0)));
  const scoreColor = score>=80?"#4ade80":score>=60?"#60a5fa":"#f87171";

  return (
    <div>
      {/* Score */}
      <div style={{...S.card, marginBottom:16, textAlign:"center"}}>
        <div style={S.statLbl}>Inventory Health Score</div>
        <div style={{display:"flex",alignItems:"baseline",justifyContent:"center",gap:4}}>
          <div style={{fontSize:56, fontWeight:800, color:scoreColor, lineHeight:1.1}}>{score}</div>
          <div style={{fontSize:22, color:T.textFaint, fontWeight:400}}>/100</div>
        </div>
        {/* Score bar */}
        <div style={{background:T.surface2,borderRadius:6,height:8,margin:"10px 0 6px",overflow:"hidden"}}>
          <div style={{width:`${score}%`,height:"100%",background:scoreColor,borderRadius:6,transition:"width 0.5s ease"}}/>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:T.textFaint,marginBottom:6}}>
          <span>0 — Critical</span><span>60 — Moderate</span><span>80 — Healthy</span><span>100</span>
        </div>
        <div style={{fontSize:12, color:T.textMuted}}>
          {score>=80?"Healthy — keep it up":score>=60?"Moderate — review aged stock":"Needs attention — too much dead stock"}
        </div>
        <div style={{fontSize:11, color:T.textFaint, marginTop:6, textAlign:"left"}}>
          Score factors: aged stock (−40pts max) · unpriced cards (−20pts max) · underwater pricing (−10pts)
        </div>
      </div>

      {/* KPI grid */}
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:16}}>
        <div style={S.stat}><div style={S.statLbl}>In Stock</div><div style={S.statVal}>{inStock.length}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Avg Days to Sell</div><div style={{...S.statVal,color:"#60a5fa"}}>{avgDaysToSell.toFixed(0)}d</div></div>
        <div style={S.stat}><div style={S.statLbl}>Dead Stock Value</div><div style={{...S.statVal,color:"#f87171"}}>{fmtT(deadValue)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Underwater</div><div style={{...S.statVal,color:"#f87171"}}>{underwater.length}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Underpriced</div><div style={{...S.statVal,color:"#fbbf24"}}>{underpriced.length}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Unpriced</div><div style={{...S.statVal,color:"#f87171"}}>{unpriced.length}</div></div>
      </div>

      {/* Cards vs. Sealed breakdown */}
      {(() => {
        const cardsStock  = inStock.filter(i => i.Type !== "Sealed Product");
        const sealedStock = inStock.filter(i => i.Type === "Sealed Product");
        const cardsValue  = cardsStock.reduce((s,i)=>s+(i.AcquisitionCost||0),0);
        const sealedValue = sealedStock.reduce((s,i)=>s+(i.AcquisitionCost||0),0);
        if (inStock.length === 0) return null;
        return (
          <div style={{...S.card, marginBottom:16}}>
            <div style={S.h2}>Cards vs. Sealed</div>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12}}>
              <div>
                <div style={S.statLbl}>Cards</div>
                <div style={S.statVal}>{cardsStock.length}</div>
                <div style={{fontSize:11, color:T.textFaint, marginTop:2}}>{fmtT(cardsValue)} tied up</div>
              </div>
              <div>
                <div style={S.statLbl}>Sealed</div>
                <div style={S.statVal}>{sealedStock.length}</div>
                <div style={{fontSize:11, color:T.textFaint, marginTop:2}}>{fmtT(sealedValue)} tied up</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Age distribution */}
      <div style={{...S.card, marginBottom:16}}>
        <div style={S.h2}>Inventory Age Distribution</div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={ageData} margin={{top:0,right:0,left:-20,bottom:0}}>
            <XAxis dataKey="label" tick={{fontSize:11,fill:T.textMuted}} />
            <YAxis tick={{fontSize:11,fill:T.textMuted}} />
            <Tooltip contentStyle={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text}} />
            <Bar dataKey="count" radius={[4,4,0,0]}>
              {ageData.map((d,i)=><Cell key={i} fill={d.fill}/>)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top dead stock */}
      {topDead.length>0 && (
        <div style={{...S.card, marginBottom:16}}>
          <div style={S.h2}>Top Dead Stock (90d+) — {fmt(deadValue)} tied up</div>
          {topDead.map(item=>(
            <div key={item.ID} style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",padding:"8px 0",borderBottom:`0.5px solid ${T.border}`}}>
              <div>
                <div style={{fontSize:13,fontWeight:500}}>{item.CardName}</div>
                <div style={{fontSize:11,color:T.textMuted}}>{item.Set} · {daysSince(item.AcquisitionDate)}d ago</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:13,color:"#f87171"}}>{fmt(item.AcquisitionCost)} cost</div>
                <div style={{fontSize:11,color:T.textFaint}}>{fmt(item.ListPrice)} list</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Underpriced opportunities */}
      {underpriced.length>0 && (
        <div style={S.card}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4}}>
            <div style={S.h2}>Underpriced Opportunities ({underpriced.length} cards)</div>
            {underpriced.length > 5 && (
              <button
                onClick={() => setShowAllUnderpriced(v=>!v)}
                style={{fontSize:11, color:"#88bdf2", background:"none", border:"none",
                  cursor:"pointer", padding:"2px 6px", fontFamily:"'Sora',sans-serif"}}>
                {showAllUnderpriced ? "Show less ↑" : `View all ${underpriced.length} →`}
              </button>
            )}
          </div>
          <div style={{fontSize:12,color:T.textMuted,marginBottom:8}}>
            Market value is 20%+ above your list price — consider raising prices
          </div>
          {(showAllUnderpriced ? underpriced : underpriced.slice(0,5)).map(item=>(
            <div key={item.ID} style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",padding:"8px 0",borderBottom:`0.5px solid ${T.border}`}}>
              <div>
                <div style={{fontSize:13,fontWeight:500}}>{item.CardName}</div>
                <div style={{fontSize:11,color:T.textMuted}}>{item.Set}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:12,color:"#4ade80"}}>MV {fmt(item.MarketValueAtAcquisition)}</div>
                <div style={{fontSize:12,color:"#f87171"}}>List {fmt(item.ListPrice)}</div>
                <div style={{fontSize:11,color:"#fbbf24"}}>
                  +{pct(((item.MarketValueAtAcquisition-item.ListPrice)/item.ListPrice)*100)} gap
                </div>
              </div>
            </div>
          ))}
          {underpriced.length > 5 && !showAllUnderpriced && (
            <div style={{fontSize:11, color:T.textFaint, textAlign:"center", marginTop:8}}>
              {underpriced.length - 5} more cards not shown —{" "}
              <button onClick={() => setShowAllUnderpriced(true)}
                style={{color:"#88bdf2", background:"none", border:"none",
                  cursor:"pointer", fontSize:11, fontFamily:"'Sora',sans-serif", padding:0}}>
                view all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── BEST / WORST SELLERS ────────────────────────────────────────────────────
function BestSellers({ inventory, S, T }) {
  const [sortBy, setSortBy]   = useState("profit");
  const [minSales, setMinSales] = useState(1);

  const sold = inventory.filter(i => i.Status === "Sold" && i.CardName);
  const cashSold   = sold.filter(i => i.SoldChannel !== "Trade");
  const tradedOut  = sold.filter(i => i.SoldChannel === "Trade");

  // Aggregate by card name + set
  const cardMap = useMemo(() => {
    const map = {};
    cashSold.forEach(item => {
      const key = `${item.CardName}||${item.Set||""}`;
      if (!map[key]) map[key] = {
        CardName: item.CardName, Set: item.Set||"", Rarity: item.Rarity||"",
        sales: 0, totalRevenue: 0, totalCost: 0, totalProfit: 0,
        avgSalePrice: 0, avgCost: 0, avgProfit: 0, avgROI: 0,
        image: item.ImageURL||"",
      };
      map[key].sales++;
      map[key].totalRevenue += parseFloat(item.SoldPrice)||0;
      map[key].totalCost    += parseFloat(item.AcquisitionCost)||0;
      map[key].totalProfit  += (parseFloat(item.SoldPrice)||0) - (parseFloat(item.AcquisitionCost)||0);
      if (item.ImageURL) map[key].image = item.ImageURL;
    });
    Object.values(map).forEach(c => {
      c.avgSalePrice = c.sales>0 ? c.totalRevenue/c.sales : 0;
      c.avgCost      = c.sales>0 ? c.totalCost/c.sales    : 0;
      c.avgProfit    = c.sales>0 ? c.totalProfit/c.sales  : 0;
      c.avgROI       = c.avgCost>0 ? (c.avgProfit/c.avgCost)*100 : 0;
    });
    return Object.values(map).filter(c => c.sales >= minSales);
  }, [cashSold, minSales]);

  const sorted = useMemo(() => {
    return [...cardMap].sort((a,b) => {
      if (sortBy==="profit")   return b.totalProfit  - a.totalProfit;
      if (sortBy==="roi")      return b.avgROI       - a.avgROI;
      if (sortBy==="revenue")  return b.totalRevenue - a.totalRevenue;
      if (sortBy==="sales")    return b.sales        - a.sales;
      if (sortBy==="worst")    return a.totalProfit  - b.totalProfit;
      return 0;
    });
  }, [cardMap, sortBy]);

  const best   = sorted.slice(0, 10);
  const worst  = [...cardMap].sort((a,b)=>a.totalProfit-b.totalProfit).slice(0,10);

  const totalSold      = cashSold.length;
  const totalRevenue   = cashSold.reduce((s,i)=>s+(parseFloat(i.SoldPrice)||0),0);
  const totalProfit    = cashSold.reduce((s,i)=>s+(parseFloat(i.SoldPrice)||0)-(parseFloat(i.AcquisitionCost)||0),0);
  const totalTradeGain = tradedOut.reduce((s,i)=>s+(parseFloat(i.TradeGain)||0),0);
  const totalTraded    = tradedOut.length;

  if (sold.length === 0) return (
    <div style={{textAlign:"center",padding:"40px 0",color:T.textFaint}}>
      No sold cards yet — data will appear here once you start selling
    </div>
  );

  return (
    <div>
      {/* Summary */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
        <div style={S.stat}><div style={S.statLbl}>Cash Sales</div><div style={S.statVal}>{totalSold}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Revenue</div><div style={{...S.statVal,color:"#4ade80"}}>{fmt(totalRevenue)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Cash Profit</div><div style={{...S.statVal,color:totalProfit>=0?"#4ade80":"#f87171"}}>{fmt(totalProfit)}</div></div>
        {totalTraded > 0 && <>
          <div style={S.stat}><div style={S.statLbl}>Traded Out</div><div style={{...S.statVal,color:"#c084fc"}}>{totalTraded}</div></div>
          <div style={S.stat}><div style={S.statLbl}>Trade Gains</div><div style={{...S.statVal,color:"#c084fc"}}>{fmt(totalTradeGain)}</div></div>
          <div style={S.stat}>
            <div style={S.statLbl}>Total Gain</div>
            <div style={{...S.statVal,color:"#4ade80"}}>{fmt(totalProfit + totalTradeGain)}</div>
          </div>
        </>}
      </div>

      {/* Cards vs. Sealed breakdown */}
      {(() => {
        const cardsSold  = cashSold.filter(i => i.Type !== "Sealed Product");
        const sealedSold = cashSold.filter(i => i.Type === "Sealed Product");
        const cardsProfit  = cardsSold.reduce((s,i)=>s+(parseFloat(i.SoldPrice)||0)-(parseFloat(i.AcquisitionCost)||0),0);
        const sealedProfit = sealedSold.reduce((s,i)=>s+(parseFloat(i.SoldPrice)||0)-(parseFloat(i.AcquisitionCost)||0),0);
        if (cashSold.length === 0) return null;
        return (
          <div style={{...S.card, marginBottom:16}}>
            <div style={S.h2}>Cards vs. Sealed</div>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:12}}>
              <div>
                <div style={S.statLbl}>Cards Sold</div>
                <div style={S.statVal}>{cardsSold.length}</div>
                <div style={{fontSize:11, color: cardsProfit>=0?"#4ade80":"#f87171", marginTop:2}}>{fmt(cardsProfit)} profit</div>
              </div>
              <div>
                <div style={S.statLbl}>Sealed Sold</div>
                <div style={S.statVal}>{sealedSold.length}</div>
                <div style={{fontSize:11, color: sealedProfit>=0?"#4ade80":"#f87171", marginTop:2}}>{fmt(sealedProfit)} profit</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        <div style={{fontSize:12,color:T.textMuted}}>Sort by:</div>
        {[["profit","Most Profit"],["roi","Best ROI"],["revenue","Most Revenue"],["sales","Most Sales"],["worst","Worst Performers"]].map(([val,label])=>(
          <button key={val} style={{...S.chip(sortBy===val),padding:"6px 12px"}}
            onClick={()=>setSortBy(val)}>{label}</button>
        ))}
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6}}>
          <div style={{fontSize:12,color:T.textMuted}}>Min sales:</div>
          <select style={{...S.select,width:"auto",fontSize:12,padding:"4px 8px"}}
            value={minSales} onChange={e=>setMinSales(parseInt(e.target.value))}>
            {[1,2,3,5,10].map(n=><option key={n} value={n}>{n}+</option>)}
          </select>
        </div>
      </div>

      {/* Best sellers list */}
      <div style={S.h2}>
        {sortBy==="worst" ? `Worst Performers (${worst.length} cards)` : `Top ${best.length} Cards`}
      </div>
      {(sortBy==="worst" ? worst : best).map((card,i)=>(
        <div key={card.CardName+card.Set} style={{...S.card,marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div style={{fontSize:18,fontWeight:700,color:T.textFaint,width:24,textAlign:"center",flexShrink:0}}>
              {i+1}
            </div>
            {card.image ? (
              <img src={card.image} alt="" style={{width:36,height:50,objectFit:"cover",borderRadius:4,flexShrink:0}}/>
            ) : (
              <div style={{width:36,height:50,borderRadius:4,border:`1px dashed ${T.border}`,
                flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:14,color:T.textFaint}}>⬡</div>
            )}
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:13,fontWeight:600}}>{card.CardName}</div>
              <div style={{fontSize:11,color:T.textMuted}}>
                {[card.Set,card.Rarity].filter(Boolean).join(" · ")}
              </div>
              <div style={{fontSize:11,color:T.textFaint,marginTop:2}}>
                {card.sales} sold · avg {fmt(card.avgSalePrice)}/card
              </div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontSize:14,fontWeight:700,color:card.totalProfit>=0?"#4ade80":"#f87171"}}>
                {fmt(card.totalProfit)}
              </div>
              <div style={{fontSize:11,color:T.textMuted}}>
                {pct(card.avgROI)} ROI
              </div>
              <div style={{fontSize:11,color:T.textFaint}}>
                {fmt(card.totalRevenue)} rev
              </div>
            </div>
          </div>
          {/* Restock recommendation */}
          {sortBy!=="worst" && card.avgROI>20 && (
            <div style={{marginTop:8,padding:"4px 10px",borderRadius:6,
              background:"#0f2e1a",border:"0.5px solid #166534",
              fontSize:11,color:"#4ade80"}}>
              ✓ Strong performer — consider restocking
            </div>
          )}
          {sortBy==="worst" && card.avgROI<0 && (
            <div style={{marginTop:8,padding:"4px 10px",borderRadius:6,
              background:"#2e0f0f",border:"0.5px solid #991b1b",
              fontSize:11,color:"#f87171"}}>
              ✗ Selling below cost — avoid buying more
            </div>
          )}
        </div>
      ))}

      {/* Chart */}
      {best.length>0 && sortBy!=="worst" && (
        <div style={{...S.card,marginTop:16}}>
          <div style={S.h2}>Profit by Card</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={best.slice(0,8).map(c=>({
              name: c.CardName.length>12?c.CardName.slice(0,12)+"…":c.CardName,
              profit: Math.round(c.totalProfit),
              revenue: Math.round(c.totalRevenue),
            }))} margin={{top:0,right:0,left:-15,bottom:40}}>
              <XAxis dataKey="name" tick={{fontSize:9,fill:T.textMuted}} angle={-35} textAnchor="end" />
              <YAxis tick={{fontSize:10,fill:T.textMuted}} />
              <Tooltip contentStyle={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text}} />
              <Bar dataKey="profit" name="Profit" fill="#4ade80" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ─── LOT ROI ──────────────────────────────────────────────────────────────────
function LotROI({ inventory, lots, S, T }) {
  const activeLots = lots.filter(l=>l.Status!=="Deleted");

  const lotStats = useMemo(()=>activeLots.map(lot=>{
    const items   = inventory.filter(i=>i.LotID===lot.ID&&i.Status!=="Deleted");
    const sold    = items.filter(i=>i.Status==="Sold");
    const inStock = items.filter(i=>i.Status==="In Stock"||i.Status==="Reserved");
    const revenue = sold.reduce((s,i)=>s+(i.SoldPrice||0),0);
    const cost    = lot.TotalPaid||0;
    const profit  = revenue-cost;
    const roi     = cost>0?((revenue-cost)/cost)*100:0;
    const recovery= cost>0?Math.min(100,(revenue/cost)*100):0;
    const avgDays = sold.length>0
      ? sold.filter(i=>i.AcquisitionDate&&i.SoldDate)
          .reduce((s,i)=>s+Math.max(0,Math.floor((new Date(i.SoldDate)-new Date(i.AcquisitionDate))/86400000)),0)
        / Math.max(1,sold.filter(i=>i.AcquisitionDate&&i.SoldDate).length)
      : 0;
    return {...lot,items,sold,inStock,revenue,cost,profit,roi,recovery,avgDays};
  }),[activeLots,inventory]);

  // By source type
  const bySource = useMemo(()=>{
    const map = {};
    lotStats.forEach(l=>{
      if(!map[l.Source]) map[l.Source]={source:l.Source,count:0,totalPaid:0,revenue:0};
      map[l.Source].count++;
      map[l.Source].totalPaid+=l.cost;
      map[l.Source].revenue+=l.revenue;
    });
    return Object.values(map).map(s=>({...s,roi:s.totalPaid>0?((s.revenue-s.totalPaid)/s.totalPaid)*100:0}));
  },[lotStats]);

  const totalInvested = lotStats.reduce((s,l)=>s+l.cost,0);
  const totalRevenue  = lotStats.reduce((s,l)=>s+l.revenue,0);
  const totalProfit   = totalRevenue-totalInvested;
  const overallROI    = totalInvested>0?((totalRevenue-totalInvested)/totalInvested)*100:0;

  return (
    <div>
      {/* Summary */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
        <div style={S.stat}><div style={S.statLbl}>Total Invested</div><div style={{...S.statVal,color:"#f87171"}}>{fmtT(totalInvested)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Total Recovered</div><div style={{...S.statVal,color:"#4ade80"}}>{fmtT(totalRevenue)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Net Profit</div><div style={{...S.statVal,color:totalProfit>=0?"#4ade80":"#f87171"}}>{fmtT(totalProfit)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Overall ROI</div><div style={{...S.statVal,color:overallROI>=0?"#4ade80":"#f87171"}}>{pct(overallROI)}</div></div>
      </div>

      {/* ROI by source type */}
      {bySource.length>0 && (
        <div style={{...S.card,marginBottom:16}}>
          <div style={S.h2}>ROI by Lot Type</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={bySource} margin={{top:0,right:0,left:-10,bottom:0}}>
              <XAxis dataKey="source" tick={{fontSize:10,fill:T.textMuted}} />
              <YAxis tickFormatter={v=>`${v.toFixed(0)}%`} tick={{fontSize:10,fill:T.textMuted}} />
              <Tooltip formatter={v=>[`${v.toFixed(1)}%`,"ROI"]}
                contentStyle={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text}} />
              <Bar dataKey="roi" radius={[4,4,0,0]}>
                {bySource.map((d,i)=><Cell key={i} fill={d.roi>=0?"#4ade80":"#f87171"}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Per lot breakdown */}
      <div style={S.h2}>Per Lot Breakdown</div>
      {lotStats.map(lot=>(
        <div key={lot.ID} style={{...S.card,marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
            <div>
              <div style={{fontSize:14,fontWeight:600}}>{lot.Name}</div>
              <div style={{fontSize:11,color:T.textMuted}}>{lot.Source} · {lot.items.length} cards · {lot.sold.length} sold</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:15,fontWeight:700,color:lot.roi>=0?"#4ade80":"#f87171"}}>{pct(lot.roi)} ROI</div>
              <div style={{fontSize:11,color:T.textFaint}}>avg {lot.avgDays.toFixed(0)}d to sell</div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:8}}>
            <div style={S.infoBox}><div style={S.statLbl}>Paid</div><div style={{fontSize:13,fontWeight:600,color:"#f87171"}}>{fmtT(lot.cost)}</div></div>
            <div style={S.infoBox}><div style={S.statLbl}>Recovered</div><div style={{fontSize:13,fontWeight:600,color:"#4ade80"}}>{fmtT(lot.revenue)}</div></div>
            <div style={S.infoBox}><div style={S.statLbl}>Remaining</div><div style={{fontSize:13,fontWeight:600,color:"#60a5fa"}}>{lot.inStock.length} cards</div></div>
          </div>
          {/* Break-even bar */}
          <div style={{height:6,borderRadius:3,background:T.border,overflow:"hidden"}}>
            <div style={{height:"100%",borderRadius:3,width:`${lot.recovery}%`,
              background:lot.recovery>=100?"#4ade80":"#3b82f6",transition:"width 0.3s"}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:T.textFaint,marginTop:3}}>
            <span>{lot.recovery.toFixed(0)}% recovered</span>
            {lot.recovery<100 && <span>{fmt(lot.cost-lot.revenue)} to break even</span>}
          </div>
        </div>
      ))}
      {lotStats.length===0 && <div style={{textAlign:"center",padding:"30px 0",color:T.textFaint}}>No lots yet</div>}
    </div>
  );
}

// ─── SHOW PERFORMANCE ─────────────────────────────────────────────────────────
function ShowPerformance({ inventory, shows, S, T }) {
  const completedShows = shows.filter(s=>s.Status==="Completed"||s.Status==="Active");

  const showStats = useMemo(()=>completedShows.map(show=>{
    const cards   = inventory.filter(i=>i.ShowID===show.ID);
    const sold    = cards.filter(i=>i.Status==="Sold");
    const revenue = sold.reduce((s,i)=>s+(i.SoldPrice||0),0);
    const cost    = sold.reduce((s,i)=>s+(i.AcquisitionCost||0),0);
    const showCosts = (parseFloat(show.TableCost)||0)+(parseFloat(show.Marketing)||0)+
                      (parseFloat(show.Travel)||0)+(parseFloat(show.Food)||0);
    const profit  = revenue-cost-showCosts;
    const margin  = revenue>0?((profit/revenue)*100):0;
    const brought = cards.filter(i=>i.Status!=="Deleted").length;
    const sellThru= brought>0?(sold.length/brought)*100:0;
    const avgSale = sold.length>0?revenue/sold.length:0;
    return {...show,cards,sold,revenue,cost,showCosts,profit,margin,brought,sellThru,avgSale};
  }),[completedShows,inventory]);

  // Chart data
  const chartData = showStats.map(s=>({
    name: s.Name.length>10?s.Name.slice(0,10)+"…":s.Name,
    revenue: Math.round(s.revenue),
    profit:  Math.round(s.profit),
    costs:   Math.round(s.showCosts),
  }));

  const totalRevenue = showStats.reduce((s,l)=>s+l.revenue,0);
  const totalProfit  = showStats.reduce((s,l)=>s+l.profit,0);
  const totalCosts   = showStats.reduce((s,l)=>s+l.showCosts,0);
  const bestShow     = [...showStats].sort((a,b)=>b.profit-a.profit)[0];

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:16}}>
        <div style={S.stat}><div style={S.statLbl}>Total Revenue</div><div style={{...S.statVal,color:"#4ade80"}}>{fmtT(totalRevenue)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Total Profit</div><div style={{...S.statVal,color:totalProfit>=0?"#4ade80":"#f87171"}}>{fmtT(totalProfit)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Show Costs</div><div style={{...S.statVal,color:"#f87171"}}>{fmtT(totalCosts)}</div></div>
      </div>

      {bestShow && (
        <div style={{...S.infoBox,marginBottom:16,borderColor:"#4ade80"}}>
          <div style={{fontSize:12,color:"#4ade80",fontWeight:500,marginBottom:4}}>🏆 Best Show</div>
          <div style={{fontSize:14,fontWeight:600}}>{bestShow.Name}</div>
          <div style={{fontSize:12,color:T.textMuted}}>
            {fmtT(bestShow.profit)} profit · {bestShow.sold.length} cards sold · {pct(bestShow.sellThru)} sell-through
          </div>
        </div>
      )}

      {chartData.length>0 && (
        <div style={{...S.card,marginBottom:16}}>
          <div style={S.h2}>Revenue vs Profit by Show</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} margin={{top:0,right:0,left:-15,bottom:0}}>
              <XAxis dataKey="name" tick={{fontSize:10,fill:T.textMuted}} />
              <YAxis tick={{fontSize:10,fill:T.textMuted}} />
              <Tooltip contentStyle={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text}} />
              <Bar dataKey="revenue" name="Revenue" fill="#3b82f6" radius={[4,4,0,0]} />
              <Bar dataKey="profit"  name="Profit"  fill="#4ade80" radius={[4,4,0,0]} />
              <Bar dataKey="costs"   name="Costs"   fill="#f87171" radius={[4,4,0,0]} />
              <Legend wrapperStyle={{fontSize:11,color:T.textMuted}} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {showStats.map(show=>(
        <div key={show.ID} style={{...S.card,marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
            <div>
              <div style={{fontSize:14,fontWeight:600}}>{show.Name}</div>
              <div style={{fontSize:11,color:T.textMuted}}>{show.StartDate}{show.Location?` · ${show.Location}`:""}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:15,fontWeight:700,color:show.profit>=0?"#4ade80":"#f87171"}}>{fmtT(show.profit)}</div>
              <div style={{fontSize:10,color:T.textFaint}}>profit</div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:8}}>
            <div style={S.infoBox}><div style={S.statLbl}>Revenue</div><div style={{fontSize:13,fontWeight:600,color:"#4ade80"}}>{fmtT(show.revenue)}</div></div>
            <div style={S.infoBox}><div style={S.statLbl}>Sold</div><div style={{fontSize:13,fontWeight:600}}>{show.sold.length}/{show.brought}</div></div>
            <div style={S.infoBox}><div style={S.statLbl}>Sell-Through</div><div style={{fontSize:13,fontWeight:600,color:"#60a5fa"}}>{pct(show.sellThru)}</div></div>
            <div style={S.infoBox}><div style={S.statLbl}>Show Costs</div><div style={{fontSize:13,fontWeight:600,color:"#f87171"}}>{fmtT(show.showCosts)}</div></div>
            <div style={S.infoBox}><div style={S.statLbl}>Avg Sale</div><div style={{fontSize:13,fontWeight:600}}>{fmtT(show.avgSale)}</div></div>
            <div style={S.infoBox}><div style={S.statLbl}>Margin</div><div style={{fontSize:13,fontWeight:600,color:show.margin>=0?"#4ade80":"#f87171"}}>{pct(show.margin)}</div></div>
          </div>
          {show.showCosts>0 && (
            <div style={{fontSize:11,color:T.textFaint}}>
              Costs: Table {fmt(show.TableCost||0)}
              {show.Marketing>0?` · Mktg ${fmt(show.Marketing)}`:""}
              {show.Travel>0?` · Travel ${fmt(show.Travel)}`:""}
              {show.Food>0?` · Food ${fmt(show.Food)}`:""}
            </div>
          )}
        </div>
      ))}
      {showStats.length===0 && <div style={{textAlign:"center",padding:"30px 0",color:T.textFaint}}>No completed shows yet</div>}
    </div>
  );
}

// ─── BUY RATE ANALYSIS ────────────────────────────────────────────────────────
function BuyRateAnalysis({ inventory, trades, S, T }) {
  const bought = inventory.filter(i=>i.Source==="Individual Buy"||i.Source==="Customer Trade");

  // Trade-ins by credit rate
  const tradesByRate = useMemo(()=>{
    const map = {};
    trades.forEach(t=>{
      const r = Math.round((parseFloat(t.CreditRate)||0.8)*100);
      if(!map[r]) map[r]={rate:r,count:0,totalCredit:0,label:`${r}%`};
      map[r].count++;
      map[r].totalCredit+=parseFloat(t.TotalCreditValue)||0;
    });
    return Object.values(map).sort((a,b)=>a.rate-b.rate);
  },[trades]);

  // Acquisition source breakdown
  const sourceMap = useMemo(()=>{
    const m = {};
    inventory.filter(i=>i.Status!=="Deleted").forEach(i=>{
      const src = i.Source||"Unknown";
      if(!m[src]) m[src]={source:src,count:0,totalCost:0,totalRevenue:0};
      m[src].count++;
      m[src].totalCost+=parseFloat(i.AcquisitionCost)||0;
      if(i.Status==="Sold") m[src].totalRevenue+=parseFloat(i.SoldPrice)||0;
    });
    return Object.values(m).sort((a,b)=>b.count-a.count);
  },[inventory]);

  // Cards acquired via trade — ROI
  const tradeCards = inventory.filter(i=>i.Source==="Customer Trade"&&i.Status==="Sold");
  const tradeSoldRevenue = tradeCards.reduce((s,i)=>s+(i.SoldPrice||0),0);
  const tradeSoldCost    = tradeCards.reduce((s,i)=>s+(i.AcquisitionCost||0),0);
  const tradeROI         = tradeSoldCost>0?((tradeSoldRevenue-tradeSoldCost)/tradeSoldCost)*100:0;

  // Individual buy ROI
  const indivCards   = inventory.filter(i=>i.Source==="Individual Buy"&&i.Status==="Sold");
  const indivRevenue = indivCards.reduce((s,i)=>s+(i.SoldPrice||0),0);
  const indivCost    = indivCards.reduce((s,i)=>s+(i.AcquisitionCost||0),0);
  const indivROI     = indivCost>0?((indivRevenue-indivCost)/indivCost)*100:0;

  const COLORS = ["#3b82f6","#4ade80","#f87171","#60a5fa","#fbbf24","#a78bfa","#34d399"];

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
        <div style={S.stat}>
          <div style={S.statLbl}>Trade-In ROI</div>
          <div style={{...S.statVal,color:tradeROI>=0?"#4ade80":"#f87171"}}>{pct(tradeROI)}</div>
          <div style={{fontSize:10,color:T.textFaint,marginTop:2}}>{tradeCards.length} sold</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>Direct Buy ROI</div>
          <div style={{...S.statVal,color:indivROI>=0?"#4ade80":"#f87171"}}>{pct(indivROI)}</div>
          <div style={{fontSize:10,color:T.textFaint,marginTop:2}}>{indivCards.length} sold</div>
        </div>
      </div>

      {/* Trades by rate */}
      {tradesByRate.length>0 && (
        <div style={{...S.card,marginBottom:16}}>
          <div style={S.h2}>Trades by Credit Rate</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={tradesByRate} margin={{top:0,right:0,left:-15,bottom:0}}>
              <XAxis dataKey="label" tick={{fontSize:11,fill:T.textMuted}} />
              <YAxis tick={{fontSize:11,fill:T.textMuted}} />
              <Tooltip contentStyle={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,color:T.text}} />
              <Bar dataKey="count" name="# Trades" fill="#3b82f6" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:8}}>
            {tradesByRate.map(r=>(
              <div key={r.rate} style={{...S.infoBox,flex:1,minWidth:80,marginBottom:0,textAlign:"center"}}>
                <div style={{fontSize:16,fontWeight:700,color:"#3b82f6"}}>{r.label}</div>
                <div style={{fontSize:11,color:T.textMuted}}>{r.count} trades</div>
                <div style={{fontSize:11,color:"#4ade80"}}>{fmtT(r.totalCredit)} credit</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Source breakdown */}
      {sourceMap.length>0 && (
        <div style={{...S.card,marginBottom:16}}>
          <div style={S.h2}>Cards by Acquisition Source</div>
          <div style={{display:"flex",alignItems:"center",gap:16}}>
            <PieChart width={120} height={120}>
              <Pie data={sourceMap} dataKey="count" nameKey="source" cx="50%" cy="50%" outerRadius={50}>
                {sourceMap.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Pie>
            </PieChart>
            <div style={{flex:1}}>
              {sourceMap.map((s,i)=>(
                <div key={s.source} style={{display:"flex",justifyContent:"space-between",
                  alignItems:"center",marginBottom:6}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:10,height:10,borderRadius:2,background:COLORS[i%COLORS.length],flexShrink:0}}/>
                    <span style={{fontSize:12}}>{s.source}</span>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <span style={{fontSize:12,fontWeight:600}}>{s.count}</span>
                    <span style={{fontSize:11,color:T.textFaint,marginLeft:6}}>{fmt(s.totalCost/Math.max(1,s.count))}/card</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {trades.length===0 && bought.length===0 && (
        <div style={{textAlign:"center",padding:"30px 0",color:T.textFaint}}>
          No trade or buy data yet — complete some trades to see analysis
        </div>
      )}
    </div>
  );
}

// ─── GRADED CARDS ─────────────────────────────────────────────────────────────
function GradedCards({ inventory, S, T }) {
  const today = new Date().toISOString().split("T")[0];

  // All cards that have ever been submitted or are currently out
  const grading     = inventory.filter(i => i.GradingCompany && i.Status !== "Sold");
  const returned    = inventory.filter(i => i.GradingCompany && i.Status === "Sold");
  const outNow      = grading.filter(i => i.Status !== "Sold");
  const overdue     = outNow.filter(i => i.GradingReturnDate && i.GradingReturnDate < today);
  const upcoming    = outNow.filter(i => i.GradingReturnDate && i.GradingReturnDate >= today);

  // Investment metrics — total cost includes grading fees
  const totalCost     = outNow.reduce((s,i) => s + (parseFloat(i.AcquisitionCost)||0) + (parseFloat(i.GradingCost)||0), 0);
  const totalCardCost = outNow.reduce((s,i) => s + (parseFloat(i.AcquisitionCost)||0), 0);
  const totalGradeFee = outNow.reduce((s,i) => s + (parseFloat(i.GradingCost)||0), 0);
  const totalEstVal   = outNow.reduce((s,i) => s + (parseFloat(i.GradingEstimatedValue)||0), 0);
  const estROI        = totalCost > 0 ? ((totalEstVal - totalCost) / totalCost * 100) : 0;

  // By company
  const byCompany = outNow.reduce((acc, i) => {
    acc[i.GradingCompany] = (acc[i.GradingCompany] || 0) + 1;
    return acc;
  }, {});

  // Sold graded cards — actual ROI
  const soldGraded = inventory.filter(i => i.GradingCompany && i.Status === "Sold" && i.SoldPrice);
  const gradedProfit = soldGraded.reduce((s,i) =>
    s + ((parseFloat(i.SoldPrice)||0) - (parseFloat(i.AcquisitionCost)||0)), 0);

  if (outNow.length === 0 && soldGraded.length === 0) return (
    <div style={{textAlign:"center",padding:"40px 0",color:T.textFaint}}>
      <div style={{fontSize:32,marginBottom:8}}>📋</div>
      <div style={{fontSize:14}}>No graded cards yet</div>
      <div style={{fontSize:12,color:T.textFaint,marginTop:4}}>
        Tag cards as "Out for Grading" in the card edit drawer
      </div>
    </div>
  );

  return (
    <div>
      {/* Summary stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:16}}>
        <div style={S.stat}><div style={S.statLbl}>Out Now</div><div style={{...S.statVal,color:"#c084fc"}}>{outNow.length}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Cards Cost</div><div style={{...S.statVal,color:"#f87171"}}>{fmt(totalCardCost)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Grading Fees</div><div style={{...S.statVal,color:"#fbbf24"}}>{fmt(totalGradeFee)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Total Invested</div><div style={{...S.statVal,color:"#f87171"}}>{fmt(totalCost)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Est. Return Value</div><div style={{...S.statVal,color:"#4ade80"}}>{fmt(totalEstVal)}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Est. ROI</div><div style={{...S.statVal,color:estROI>=0?"#4ade80":"#f87171"}}>{estROI.toFixed(0)}%</div></div>
        <div style={S.stat}><div style={S.statLbl}>Overdue</div><div style={{...S.statVal,color:overdue.length>0?"#f87171":T.textMuted}}>{overdue.length}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Sold (Graded)</div><div style={{...S.statVal,color:"#4ade80"}}>{soldGraded.length}</div></div>
        <div style={S.stat}><div style={S.statLbl}>Graded Profit</div><div style={{...S.statVal,color:"#4ade80"}}>{fmt(gradedProfit)}</div></div>
      </div>

      {/* By company */}
      {Object.keys(byCompany).length > 0 && (
        <div style={{...S.card,marginBottom:16}}>
          <div style={S.h2}>By Company</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:8}}>
            {Object.entries(byCompany).map(([co,count])=>(
              <div key={co} style={{background:T.surface2,borderRadius:8,padding:"8px 14px",
                border:`1px solid ${T.border}`}}>
                <div style={{fontSize:14,fontWeight:700,color:"#c084fc"}}>{count}</div>
                <div style={{fontSize:11,color:T.textMuted}}>{co}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overdue */}
      {overdue.length > 0 && (
        <div style={{...S.card,marginBottom:16,borderColor:"#f87171",background:"rgba(239,68,68,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#f87171",marginBottom:8}}>
            ⚠ {overdue.length} Overdue
          </div>
          {overdue.map(item=>(
            <div key={item.ID} style={{padding:"8px 0",borderBottom:`0.5px solid ${T.border}`}}>
              <div style={{display:"flex",justifyContent:"space-between"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:500}}>{item.CardName}</div>
                  <div style={{fontSize:11,color:T.textMuted}}>{item.Set} · {item.GradingCompany}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontSize:11,color:"#f87171"}}>Due: {item.GradingReturnDate}</div>
                  <div style={{fontSize:11,color:T.textFaint}}>Cost: {fmt(item.AcquisitionCost)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cards out now */}
      {upcoming.length > 0 && (
        <div style={{...S.card,marginBottom:16}}>
          <div style={S.h2}>Out for Grading ({upcoming.length})</div>
          {upcoming.map(item=>{
            const cost    = parseFloat(item.AcquisitionCost)||0;
            const gradeFee= parseFloat(item.GradingCost)||0;
            const totalIn = cost + gradeFee;
            const est     = parseFloat(item.GradingEstimatedValue)||0;
            const roi     = totalIn > 0 && est > 0 ? ((est-totalIn)/totalIn*100) : null;
            return (
              <div key={item.ID} style={{padding:"10px 0",borderBottom:`0.5px solid ${T.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:600}}>{item.CardName}</div>
                    <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>
                      {item.Set}{item.Number ? ` · #${item.Number}` : ""}
                    </div>
                    <div style={{fontSize:11,color:"#c084fc",marginTop:2}}>
                      📋 {item.GradingCompany}
                      {item.GradingSentDate && ` · Sent ${item.GradingSentDate}`}
                      {item.GradingReturnDate && ` · Return ${item.GradingReturnDate}`}
                    </div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
                    <div style={{fontSize:12,color:T.textMuted}}>Card cost</div>
                    <div style={{fontSize:14,fontWeight:700,color:"#f87171"}}>{fmt(cost)}</div>
                    {gradeFee > 0 && (
                      <div style={{fontSize:11,color:"#fbbf24"}}>+{fmt(gradeFee)} grading</div>
                    )}
                    {est > 0 && (
                      <>
                        <div style={{fontSize:12,color:T.textMuted,marginTop:4}}>Est. value</div>
                        <div style={{fontSize:14,fontWeight:700,color:"#4ade80"}}>{fmt(est)}</div>
                        {roi !== null && (
                          <div style={{fontSize:10,color:"#4ade80"}}>{roi>=0?"+":""}{roi.toFixed(0)}% ROI</div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sold graded cards */}
      {soldGraded.length > 0 && (
        <div style={{...S.card,marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={S.h2}>Sold Graded Cards ({soldGraded.length})</div>
            <span style={{fontSize:12,color:"#4ade80",fontWeight:600}}>{fmt(gradedProfit)} profit</span>
          </div>
          {soldGraded.map(item=>{
            const cost   = parseFloat(item.AcquisitionCost)||0;
            const sold   = parseFloat(item.SoldPrice)||0;
            const profit = sold - cost;
            const roi    = cost > 0 ? (profit/cost*100) : 0;
            return (
              <div key={item.ID} style={{padding:"8px 0",borderBottom:`0.5px solid ${T.border}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:500}}>{item.CardName}</div>
                    <div style={{fontSize:11,color:T.textMuted}}>{item.Set}</div>
                    <div style={{fontSize:11,color:"#c084fc"}}>
                      {item.GradingCompany}{item.Grade ? ` · ${item.Grade}` : ""}
                    </div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
                    <div style={{fontSize:13,fontWeight:700,color:"#4ade80"}}>{fmt(sold)}</div>
                    <div style={{fontSize:11,color:profit>=0?"#4ade80":"#f87171"}}>
                      {profit>=0?"+":""}{fmt(profit)} ({roi>=0?"+":""}{roi.toFixed(0)}%)
                    </div>
                    <div style={{fontSize:10,color:T.textFaint}}>{item.SoldDate||""}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── MARKET PRICING ──────────────────────────────────────────────────────────

// ─── CONSIGNMENT REQUESTS ────────────────────────────────────────────────────
// ─── CONSIGNMENT INVENTORY REPORT ─────────────────────────────────────────────
function ConsignmentInventory({ inventory, vendor, S, T }) {
  const commissionRate = parseFloat(vendor?.consign_settings?.commission_rate) || 0;
  const fmt = n => `$${(+n||0).toFixed(2)}`;
  const daysSince = d => d ? Math.floor((Date.now()-new Date(d).getTime())/86400000) : null;

  const cards = (inventory||[]).filter(i => i.IsConsignment);

  const STATUS_LABELS = {
    null: "In Stock",
    "":   "In Stock",
    sold_awaiting_payout: "Sold — Awaiting Payout",
    paid_out:             "Paid Out",
    returned_unsold:      "Returned Unsold",
  };
  const STATUS_COLORS = {
    null: T.textMuted, "": T.textMuted,
    sold_awaiting_payout: "#60a5fa",
    paid_out:             "#4ade80",
    returned_unsold:      T.textFaint,
  };

  const totalSold    = cards.filter(c=>c.Status==="Sold").reduce((s,c)=>s+(c.SoldPrice||0),0);
  const totalCommission = totalSold * commissionRate / 100;
  const awaitingPayout  = cards.filter(c=>c.ConsignPayoutStatus==="sold_awaiting_payout");
  const paidOut         = cards.filter(c=>c.ConsignPayoutStatus==="paid_out");
  const returned        = cards.filter(c=>c.ConsignPayoutStatus==="returned_unsold");
  const inStock         = cards.filter(c=>c.Status==="In Stock"||c.Status==="Reserved");

  return (
    <div style={S.page}>
      <div style={{...S.h1, marginBottom:4}}>💎 Consignment Inventory</div>
      <div style={{fontSize:13, color:T.textMuted, marginBottom:20}}>
        All consigned cards, payout statuses, days held, and commission earned.
      </div>

      {/* Summary stats */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",
        gap:10, marginBottom:20}}>
        {[
          ["Total Cards",       cards.length,             T.text],
          ["In Stock",          inStock.length,            T.textMuted],
          ["Awaiting Payout",   awaitingPayout.length,     "#60a5fa"],
          ["Paid Out",          paidOut.length,            "#4ade80"],
          ["Returned",          returned.length,           T.textFaint],
          ["Total Sales",       fmt(totalSold),            T.text],
          [`Commission (${commissionRate}%)`, fmt(totalCommission), "#4ade80"],
        ].map(([label, val, color])=>(
          <div key={label} style={{...S.card, padding:"10px 14px", textAlign:"center"}}>
            <div style={{fontSize:18, fontWeight:700, color, fontFamily:"'DM Mono',monospace",
              marginBottom:3}}>{val}</div>
            <div style={{fontSize:11, color:T.textMuted, textTransform:"uppercase",
              letterSpacing:"0.04em"}}>{label}</div>
          </div>
        ))}
      </div>

      {cards.length === 0 ? (
        <div style={{...S.card, textAlign:"center", padding:40}}>
          <div style={{fontSize:32, marginBottom:10}}>💎</div>
          <div style={{fontSize:15, fontWeight:600, marginBottom:6}}>No consignment cards yet</div>
          <div style={{fontSize:13, color:T.textMuted}}>
            Mark cards as consignment in the card details drawer.
          </div>
        </div>
      ) : (
        <div style={{display:"flex", flexDirection:"column", gap:8}}>
          {cards
            .sort((a,b) => {
              // Sort: awaiting payout first, then in stock, then paid/returned
              const order = {sold_awaiting_payout:0, null:1, "":1, paid_out:2, returned_unsold:3};
              return (order[a.ConsignPayoutStatus]??1) - (order[b.ConsignPayoutStatus]??1);
            })
            .map(card => {
              const days    = daysSince(card.AcquisitionDate);
              const soldAmt = card.SoldPrice || card.ListPrice || 0;
              const commission = soldAmt * commissionRate / 100;
              const status  = card.ConsignPayoutStatus || null;
              return (
                <div key={card.ID} style={{...S.card, padding:"12px 14px"}}>
                  <div style={{display:"flex", justifyContent:"space-between",
                    alignItems:"flex-start", gap:8, marginBottom:6}}>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:14, fontWeight:600, marginBottom:2,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                        {card.CardName}
                      </div>
                      <div style={{fontSize:12, color:T.textMuted}}>
                        {card.Set}{card.Number ? ` · ${card.Number}` : ""} · {card.Condition}
                      </div>
                    </div>
                    <div style={{textAlign:"right", flexShrink:0}}>
                      <div style={{fontSize:13, fontWeight:700}}>
                        {fmt(card.ListPrice)}
                      </div>
                      {card.Status === "Sold" && commissionRate > 0 && (
                        <div style={{fontSize:11, color:"#4ade80", fontWeight:600}}>
                          +{fmt(commission)} commission
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{display:"flex", justifyContent:"space-between",
                    alignItems:"center", flexWrap:"wrap", gap:6}}>
                    <span style={{
                      fontSize:11, fontWeight:700, borderRadius:6, padding:"3px 8px",
                      background:`${STATUS_COLORS[status] || T.textMuted}18`,
                      color: STATUS_COLORS[status] || T.textMuted,
                      border:`0.5px solid ${STATUS_COLORS[status] || T.textMuted}44`,
                    }}>
                      {STATUS_LABELS[status] || "In Stock"}
                    </span>
                    <div style={{display:"flex", gap:12, fontSize:11, color:T.textFaint}}>
                      {days !== null && <span>🗓 {days} day{days!==1?"s":""} held</span>}
                      {card.ConsignorID && <span>👤 Consignor on file</span>}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

function ConsignmentRequests({ S, T }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState("all");

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from("consignment_requests")
        .select("*")
        .order("created_at", { ascending: false });
      setRequests(data || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchRequests(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return requests;
    return requests.filter(r => (r.status || "pending") === filter);
  }, [requests, filter]);

  const counts = useMemo(() => ({
    pending:   requests.filter(r => (r.status||"pending") === "pending").length,
    contacted: requests.filter(r => r.status === "contacted").length,
    accepted:  requests.filter(r => r.status === "accepted").length,
    declined:  requests.filter(r => r.status === "declined").length,
  }), [requests]);

  return (
    <div>
      <div style={{...S.card, marginBottom:12}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:8}}>
          <div>
            <div style={{fontSize:14, fontWeight:700, marginBottom:4}}>💎 Consignment Sign-ups</div>
            <div style={{fontSize:12, color:T.textMuted}}>
              Submissions from the kiosk "Consign Your Card" form. We follow up within 48 hours.
            </div>
          </div>
          <button style={{...S.btn("ghost"), fontSize:12, padding:"6px 12px"}} onClick={fetchRequests}>
            Refresh
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(110px,1fr))", gap:8, marginBottom:12}}>
        {[
          ["all","All",requests.length,T.textMuted],
          ["pending","Pending",counts.pending,"#fbbf24"],
          ["contacted","Contacted",counts.contacted,"#3b82f6"],
          ["accepted","Accepted",counts.accepted,"#4ade80"],
          ["declined","Declined",counts.declined,"#f87171"],
        ].map(([key,label,count,color])=>(
          <button key={key} onClick={()=>setFilter(key)}
            style={{...S.stat, cursor:"pointer", textAlign:"left",
              border:filter===key?`1px solid ${color}`:`0.5px solid ${T.border}`}}>
            <div style={S.statLbl}>{label}</div>
            <div style={{...S.statVal, color}}>{count}</div>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{...S.card, textAlign:"center", padding:32, color:T.textMuted}}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{...S.card, textAlign:"center", padding:32, color:T.textMuted}}>
          {filter === "all" ? "No consignment sign-ups yet." : `No ${filter} sign-ups.`}
        </div>
      ) : filtered.map(r => (
        <div key={r.id} style={{...S.card, marginBottom:8}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6}}>
            <div style={{fontWeight:600, fontSize:14}}>{r.full_name}</div>
            <div style={{fontSize:10, color:T.textFaint}}>
              {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
            </div>
          </div>
          <div style={{fontSize:13, color:T.textMuted, marginBottom:6}}>
            ✉️ {r.email}
          </div>
          <div style={{display:"flex", gap:12, fontSize:12, color:T.textMuted, marginBottom:8}}>
            <span>📦 {r.card_count || "—"} card(s)</span>
            <span>💰 Est. value: {r.estimated_value ? `$${r.estimated_value}` : "—"}</span>
          </div>
          <select
            style={{fontSize:11, background:T.surface2, border:`0.5px solid ${T.border}`,
              borderRadius:6, color:T.text, padding:"4px 8px", cursor:"pointer"}}
            value={r.status || "pending"}
            onChange={async (e) => {
              await supabase.from("consignment_requests").update({ status: e.target.value }).eq("id", r.id);
              setRequests(reqs => reqs.map(x => x.id === r.id ? { ...x, status: e.target.value } : x));
            }}>
            <option value="pending">Pending</option>
            <option value="contacted">Contacted</option>
            <option value="accepted">Accepted</option>
            <option value="declined">Declined</option>
          </select>
        </div>
      ))}
    </div>
  );
}

// ─── KIOSK ANALYTICS ─────────────────────────────────────────────────────────
function KioskAnalytics({ S, T, buyers = [], onAddBuyer, vendor }) {
  const [events, setEvents]         = useState([]);
  const [sales, setSales]           = useState([]);
  const [cardRequests, setCardRequests] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showFilter, setShowFilter] = useState("all");
  const [activeTab, setActiveTab]   = useState("searches"); // searches | views | attribution | zeros | requests
  const [addingBuyer, setAddingBuyer] = useState(null); // request id being converted

  const fetchData = async () => {
    setLoading(true);
    try {
      const [evtRes, saleRes, reqRes] = await Promise.all([
        supabase.from("kiosk_events")
          .select("*")
          .eq("vendor_id", vendor?.id)
          .order("created_at", { ascending: false })
          .limit(2000),
        supabase.from("inventory")
          .select("id,card_name,set_name,sold_price,sold_at,show_id,show_name")
          .eq("vendor_id", vendor?.id)
          .eq("status", "Sold")
          .not("sold_at", "is", null)
          .order("sold_at", { ascending: false })
          .limit(1000),
        supabase.from("card_requests")
          .select("*")
          .eq("vendor_id", vendor?.id)
          .order("created_at", { ascending: false })
          .limit(500),
      ]);
      setEvents(evtRes.data || []);
      setSales(saleRes.data || []);
      setCardRequests(reqRes.data || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  // All unique shows
  const shows = useMemo(() => {
    const s = new Map();
    events.forEach(e => { if (e.show_id && e.show_name) s.set(e.show_id, e.show_name); });
    return [["all","All Shows"], ...Array.from(s.entries()).map(([id,name])=>[id,name])];
  }, [events]);

  const filtered = useMemo(() => {
    if (showFilter === "all") return events;
    return events.filter(e => e.show_id === showFilter);
  }, [events, showFilter]);

  // Top searches ranked
  const topSearches = useMemo(() => {
    const map = new Map();
    filtered.filter(e => e.event_type === "search").forEach(e => {
      const t = (e.value||"").toLowerCase().trim();
      if (!t) return;
      const existing = map.get(t) || { term: e.value, count: 0, hasResults: 0, noResults: 0 };
      existing.count++;
      if ((e.result_count||0) > 0) existing.hasResults++;
      else existing.noResults++;
      map.set(t, existing);
    });
    return Array.from(map.values()).sort((a,b) => b.count - a.count);
  }, [filtered]);

  // Zero-result searches — restocking signals
  const zeroResults = useMemo(() => {
    const map = new Map();
    filtered.filter(e => e.event_type === "search_no_results").forEach(e => {
      const t = (e.value||"").toLowerCase().trim();
      if (!t) return;
      const ex = map.get(t) || { term: e.value, count: 0, shows: new Set() };
      ex.count++;
      if (e.show_name) ex.shows.add(e.show_name);
      map.set(t, ex);
    });
    return Array.from(map.values()).sort((a,b) => b.count - a.count);
  }, [filtered]);

  // Most viewed cards
  const topViews = useMemo(() => {
    const map = new Map();
    filtered.filter(e => e.event_type === "card_view").forEach(e => {
      const key = e.card_id || e.value;
      if (!key) return;
      const ex = map.get(key) || { name: e.value, cardId: e.card_id, count: 0 };
      ex.count++;
      map.set(key, ex);
    });
    return Array.from(map.values()).sort((a,b) => b.count - a.count);
  }, [filtered]);

  // Kiosk-assisted sales — search within 5 min of sale, fuzzy name match
  const assistedSales = useMemo(() => {
    const searches = filtered.filter(e => e.event_type === "search" && (e.result_count||0) > 0);
    const WINDOW_MS = 5 * 60 * 1000;
    const results = [];
    const seen = new Set();

    sales.forEach(sale => {
      if (!sale.sold_at) return;
      const saleTime = new Date(sale.sold_at).getTime();
      const match = searches.find(s => {
        const searchTime = new Date(s.created_at).getTime();
        const term = (s.value||"").toLowerCase();
        const cardName = (sale.card_name||"").toLowerCase();
        const withinWindow = saleTime >= searchTime && saleTime <= searchTime + WINDOW_MS;
        const nameMatch = cardName.includes(term) || term.includes(cardName.split(" ")[0]);
        const sameShow = !s.show_id || s.show_id === sale.show_id;
        return withinWindow && nameMatch && sameShow;
      });
      if (match && !seen.has(sale.id)) {
        seen.add(sale.id);
        const minutesAfter = Math.round((new Date(sale.sold_at) - new Date(match.created_at)) / 60000);
        results.push({ ...sale, searchTerm: match.value, minutesAfter });
      }
    });
    return results.sort((a,b) => new Date(b.sold_at) - new Date(a.sold_at));
  }, [filtered, sales]);

  const totalSearches   = filtered.filter(e => e.event_type === "search").length;
  const totalViews      = filtered.filter(e => e.event_type === "card_view").length;
  const assistedRevenue = assistedSales.reduce((s,sale) => s+(parseFloat(sale.sold_price)||0), 0);

  const fmt = n => `$${(+n||0).toFixed(2)}`;
  const fmtDate = d => d ? new Date(d).toLocaleString() : "—";

  if (loading) return <div style={{...S.card, textAlign:"center", padding:40, color:T.textMuted}}>Loading analytics…</div>;

  return (
    <div>
      {/* Header */}
      <div style={{...S.card, marginBottom:12}}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:8}}>
          <div>
            <div style={{fontSize:14, fontWeight:700, marginBottom:4}}>📊 Kiosk Analytics</div>
            <div style={{fontSize:12, color:T.textMuted}}>
              Customer search behavior, card views, and sale attribution from the kiosk.
            </div>
          </div>
          <div style={{display:"flex", gap:8, alignItems:"center"}}>
            <select style={{...S.select, fontSize:12, padding:"6px 10px"}}
              value={showFilter} onChange={e=>setShowFilter(e.target.value)}>
              {shows.map(([id,name]) => <option key={id} value={id}>{name}</option>)}
            </select>
            <button style={{...S.btn("ghost"), fontSize:12, padding:"6px 12px"}} onClick={fetchData}>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Summary tiles */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(110px,1fr))", gap:8, marginBottom:12}}>
        <div style={S.stat}>
          <div style={S.statLbl}>Searches</div>
          <div style={S.statVal}>{totalSearches}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>Card Views</div>
          <div style={S.statVal}>{totalViews}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>Zero Results</div>
          <div style={{...S.statVal, color:"#f87171"}}>{zeroResults.length}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>Kiosk Sales</div>
          <div style={{...S.statVal, color:"#4ade80"}}>{assistedSales.length}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>Kiosk Revenue</div>
          <div style={{...S.statVal, color:"#4ade80", fontSize:14}}>{fmt(assistedRevenue)}</div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{display:"flex", gap:6, marginBottom:12, flexWrap:"wrap"}}>
        {[
          ["searches", `🔍 Top Searches (${topSearches.length})`],
          ["views",    `👁 Most Viewed (${topViews.length})`],
          ["attribution", `💰 Kiosk Sales (${assistedSales.length})`],
          ["zeros",    `🚨 Missing Inventory (${zeroResults.length})`],
          ["requests", `📥 Card Requests (${cardRequests.length})`],
        ].map(([key, label]) => (
          <button key={key} style={{...S.chip(activeTab===key), padding:"7px 12px", fontSize:12}}
            onClick={()=>setActiveTab(key)}>{label}</button>
        ))}
      </div>

      {/* ── TOP SEARCHES ───────────────────────────────────────────────────── */}
      {activeTab === "searches" && (
        topSearches.length === 0
          ? <div style={{...S.card, textAlign:"center", padding:32, color:T.textMuted}}>
              No searches logged yet. Make sure the kiosk is deployed with the latest version.
            </div>
          : topSearches.map((s, i) => (
            <div key={s.term} style={{...S.card, marginBottom:6,
              display:"flex", alignItems:"center", gap:12}}>
              <div style={{fontSize:20, fontWeight:800, color:T.textFaint,
                minWidth:28, textAlign:"right"}}>{i+1}</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:600, fontSize:14}}>{s.term}</div>
                <div style={{fontSize:11, color:T.textMuted, marginTop:2}}>
                  {s.count} search{s.count!==1?"es":""} · {s.hasResults} found results · {s.noResults} found nothing
                </div>
              </div>
              {s.noResults > 0 && (
                <div style={{fontSize:10, color:"#f87171", background:"rgba(248,113,113,0.1)",
                  border:"0.5px solid rgba(248,113,113,0.3)", borderRadius:6, padding:"3px 8px"}}>
                  Restock?
                </div>
              )}
            </div>
          ))
      )}

      {/* ── MOST VIEWED ────────────────────────────────────────────────────── */}
      {activeTab === "views" && (
        topViews.length === 0
          ? <div style={{...S.card, textAlign:"center", padding:32, color:T.textMuted}}>No card views logged yet.</div>
          : topViews.map((v, i) => (
            <div key={v.cardId||v.name} style={{...S.card, marginBottom:6,
              display:"flex", alignItems:"center", gap:12}}>
              <div style={{fontSize:20, fontWeight:800, color:T.textFaint,
                minWidth:28, textAlign:"right"}}>{i+1}</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:600, fontSize:14}}>{v.name}</div>
              </div>
              <div style={{fontSize:14, fontWeight:700, color:"#3b82f6"}}>{v.count}×</div>
            </div>
          ))
      )}

      {/* ── KIOSK-ASSISTED SALES ───────────────────────────────────────────── */}
      {activeTab === "attribution" && (
        assistedSales.length === 0
          ? <div style={{...S.card, padding:32, color:T.textMuted}}>
              <div style={{textAlign:"center", fontSize:14, marginBottom:8}}>No kiosk-assisted sales found.</div>
              <div style={{fontSize:12, color:T.textFaint, textAlign:"center"}}>
                This report matches kiosk searches to sales within 5 minutes. Make sure cards are
                being sold via Show Mode (which logs the exact sale timestamp).
              </div>
            </div>
          : <>
            <div style={{...S.infoBox, marginBottom:12}}>
              <div style={{fontSize:12, color:T.textMuted}}>
                A sale is counted as kiosk-assisted when a customer searched for a term matching the
                card name within 5 minutes before the sale. This is a conservative estimate — actual
                kiosk influence is likely higher.
              </div>
            </div>
            {assistedSales.map(sale => (
              <div key={sale.id} style={{...S.card, marginBottom:6}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4}}>
                  <div>
                    <div style={{fontWeight:600, fontSize:14}}>{sale.card_name}</div>
                    <div style={{fontSize:11, color:T.textMuted}}>
                      {sale.set_name} · {sale.show_name || "Unknown show"}
                    </div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:15, fontWeight:700, color:"#4ade80"}}>${sale.sold_price}</div>
                  </div>
                </div>
                <div style={{fontSize:11, color:T.textFaint, display:"flex", gap:12, flexWrap:"wrap"}}>
                  <span>🔍 Searched: "<strong style={{color:T.textMuted}}>{sale.searchTerm}</strong>"</span>
                  <span>⏱ Sold {sale.minutesAfter} min after search</span>
                  <span>🕐 {fmtDate(sale.sold_at)}</span>
                </div>
              </div>
            ))}
          </>
      )}

      {/* ── ZERO RESULTS = RESTOCK SIGNALS ─────────────────────────────────── */}
      {activeTab === "zeros" && (
        zeroResults.length === 0
          ? <div style={{...S.card, textAlign:"center", padding:32, color:T.textMuted}}>
              No zero-result searches yet — great inventory coverage!
            </div>
          : <>
            <div style={{...S.infoBox, marginBottom:12, borderColor:"rgba(248,113,113,0.3)"}}>
              <div style={{fontSize:12, color:"#f87171"}}>
                🚨 These are cards customers searched for but found nothing. Each one is a direct
                buying signal — consider stocking these for your next show.
              </div>
            </div>
            {zeroResults.map((z, i) => (
              <div key={z.term} style={{...S.card, marginBottom:6,
                display:"flex", alignItems:"center", gap:12,
                borderColor:"rgba(248,113,113,0.25)", background:"rgba(248,113,113,0.04)"}}>
                <div style={{fontSize:20, fontWeight:800, color:"#f87171",
                  minWidth:28, textAlign:"right"}}>{i+1}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:600, fontSize:14}}>{z.term}</div>
                  <div style={{fontSize:11, color:T.textMuted, marginTop:2}}>
                    Searched {z.count} time{z.count!==1?"s":""}
                    {z.shows.size > 0 ? ` · ${Array.from(z.shows).join(", ")}` : ""}
                  </div>
                </div>
                <div style={{fontSize:10, color:"#f87171", background:"rgba(248,113,113,0.1)",
                  border:"0.5px solid rgba(248,113,113,0.3)", borderRadius:6, padding:"4px 10px",
                  fontWeight:700}}>
                  BUY SIGNAL
                </div>
              </div>
            ))}
          </>
      )}

      {activeTab === "requests" && (
        cardRequests.length === 0 ? (
          <div style={{...S.card, textAlign:"center", padding:32, color:T.textMuted}}>
            No card requests from the kiosk yet.
          </div>
        ) : (
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {cardRequests.map(r => {
              const alreadyBuyer = buyers.some(b =>
                b.FullName?.toLowerCase() === (r.name||"").toLowerCase() ||
                (r.contact && (b.Phone === r.contact || b.Email?.toLowerCase() === r.contact.toLowerCase()))
              );
              return (
                <div key={r.id} style={{...S.card}}>
                  <div style={{display:"flex", justifyContent:"space-between",
                    alignItems:"flex-start", marginBottom:6}}>
                    <div>
                      <div style={{fontSize:14, fontWeight:700}}>{r.card || "Unknown card"}</div>
                      <div style={{fontSize:12, color:T.textMuted, marginTop:2}}>
                        {r.name && <span style={{marginRight:8}}>👤 {r.name}</span>}
                        {r.contact && <span style={{marginRight:8}}>📞 {r.contact}</span>}
                        {r.budget && <span>💰 Budget: ${r.budget}</span>}
                      </div>
                      {(r.condition || r.language) && (
                        <div style={{fontSize:11, color:T.textFaint, marginTop:2}}>
                          {[r.condition, r.language].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                    <div style={{fontSize:10, color:T.textFaint, flexShrink:0, marginLeft:8}}>
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                    </div>
                  </div>
                  {alreadyBuyer ? (
                    <div style={{fontSize:11, color:"#7EAF5C", fontWeight:600}}>
                      ✓ Already in Buyers List
                    </div>
                  ) : onAddBuyer && r.name ? (
                    <button
                      disabled={addingBuyer === r.id}
                      onClick={async () => {
                        setAddingBuyer(r.id);
                        try {
                          const created = await onAddBuyer({
                            fullName:       r.name,
                            phone:          r.contact && !r.contact.includes("@") ? r.contact : null,
                            email:          r.contact &&  r.contact.includes("@") ? r.contact : null,
                            notes:          r.card ? `Looking for: ${r.card}` : "",
                            kioskRequestId: r.id,
                          });
                          if (r.card && created?.ID) {
                            await addWantItem(created.ID, {
                              cardName: r.card,
                              maxPrice: r.budget || null,
                              notes:    [r.condition, r.language].filter(Boolean).join(", "),
                            });
                          }
                        } catch(e) { console.error("Failed to add buyer:", e.message); }
                        setAddingBuyer(null);
                      }}
                      style={{
                        background:"rgba(0,91,186,0.12)", border:"0.5px solid rgba(0,91,186,0.4)",
                        color:"#60a5fa", borderRadius:8, fontSize:12, fontWeight:600,
                        padding:"7px 14px", cursor:"pointer", width:"100%", marginTop:4,
                      }}>
                      {addingBuyer === r.id ? "Adding…" : "👥 Add to Buyers List"}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}

// ─── SALES & TAX SUMMARY ────────────────────────────────────────────────────

function StatCard({ label, value, sub, color, icon, T }) {
  return (
    <div style={{
      background:T.surface2, border:`0.5px solid ${T.border}`,
      borderRadius:14, padding:"16px 18px", flex:1, minWidth:130,
    }}>
      <div style={{fontSize:11, color:T.textMuted, fontWeight:600,
        textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6,
        display:"flex", alignItems:"center", gap:6}}>
        {icon && <span>{icon}</span>}{label}
      </div>
      <div style={{fontSize:22, fontWeight:700, color: color||T.text}}>{value}</div>
      {sub && <div style={{fontSize:11, color:T.textFaint, marginTop:4}}>{sub}</div>}
    </div>
  );
}

function SectionHeader({ title, sub, T }) {
  return (
    <div style={{marginBottom:12, marginTop:28}}>
      <div style={{fontSize:14, fontWeight:700, color:T.text}}>{title}</div>
      {sub && <div style={{fontSize:12, color:T.textMuted, marginTop:2}}>{sub}</div>}
    </div>
  );
}

function saleChannelLabel(item) {
  if (item.SoldChannel === "Store-Kiosk") return "Store";
  if (item.ShowName) return item.ShowName;
  if (item.SoldChannel === "Trade") return null; // trades excluded — no tax, not a cash sale
  return "Manual Sale";
}

const PRICE_BUCKETS = [
  { label:"$5 & Under",       min:0,    max:5 },
  { label:"$5 - $20",         min:5,    max:20 },
  { label:"$20 - $50",        min:20,   max:50 },
  { label:"$50 - $150",       min:50,   max:150 },
  { label:"$150 - $300",      min:150,  max:300 },
  { label:"$300 - $1,000",    min:300,  max:1000 },
  { label:"$1,000 - $5,000",  min:1000, max:5000 },
  { label:"$5,000+",          min:5000, max:Infinity },
];

const BUCKET_COLORS = ["#60a5fa","#4ade80","#fbbf24","#f97316","#a78bfa","#f472b6","#38bdf8","#f87171"];

function SalesByPriceRange({ inventory, isMobile, S, T }) {
  const [customMin, setCustomMin] = useState("");
  const [customMax, setCustomMax] = useState("");

  const sold = useMemo(() => inventory.filter(i => i.Status === "Sold" && i.SoldPrice != null), [inventory]);
  const onHand = useMemo(() => inventory.filter(i => i.Status === "In Stock" || i.Status === "Reserved"), [inventory]);
  const totalCount = sold.length;

  const bucketData = useMemo(() => PRICE_BUCKETS.map(b => {
    const matches = sold.filter(i => {
      const p = parseFloat(i.SoldPrice) || 0;
      return p > b.min && p <= b.max;
    });
    const onHandMatches = onHand.filter(i => {
      const p = parseFloat(i.ListPrice) || 0;
      return p > b.min && p <= b.max;
    });
    const everCount = matches.length + onHandMatches.length;
    return {
      ...b,
      count: matches.length,
      onHand: onHandMatches.length,
      sellThrough: everCount > 0 ? (matches.length / everCount * 100) : null,
      total: matches.reduce((s,i) => s + (parseFloat(i.SoldPrice)||0), 0),
      percent: totalCount ? (matches.length / totalCount * 100) : 0,
    };
  }), [sold, onHand, totalCount]);

  const customResult = useMemo(() => {
    if (customMin === "" && customMax === "") return null;
    const min = customMin === "" ? -Infinity : parseFloat(customMin);
    const max = customMax === "" ? Infinity : parseFloat(customMax);
    const matches = sold.filter(i => {
      const p = parseFloat(i.SoldPrice) || 0;
      return p >= min && p <= max;
    });
    return {
      count: matches.length,
      total: matches.reduce((s,i) => s + (parseFloat(i.SoldPrice)||0), 0),
      percent: totalCount ? (matches.length / totalCount * 100) : 0,
    };
  }, [sold, customMin, customMax, totalCount]);

  return (
    <div>
      <SectionHeader T={T} title="🥧 Sales by Price Range" sub={`${totalCount} card${totalCount!==1?"s":""} sold`} />

      {totalCount === 0 ? (
        <div style={{textAlign:"center", padding:40, color:T.textMuted}}>No sold cards yet.</div>
      ) : (
        <>
        <div style={{...S.card, marginBottom:16, display:"flex", justifyContent:"center"}}>
          <ResponsiveContainer width="100%" height={isMobile ? 260 : 320}>
            <PieChart>
              <Pie data={bucketData} dataKey="count" nameKey="label" cx="50%" cy="50%"
                outerRadius={110} label={d => d.count > 0 ? `${d.percent.toFixed(0)}%` : ""}>
                {bucketData.map((b,i) => <Cell key={b.label} fill={BUCKET_COLORS[i % BUCKET_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v,n,p) => [`${v} cards (${p.payload.percent.toFixed(1)}%)`, p.payload.label]} />
              {!isMobile && <Legend />}
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div style={{...S.card, padding:0, marginBottom:16}}>
          <div style={{display:"grid", gridTemplateColumns:"1.2fr 0.6fr 0.7fr 1fr 0.8fr", gap:8,
            padding:"10px 16px", fontSize:11, color:T.textFaint, textTransform:"uppercase",
            letterSpacing:"0.05em", borderBottom:`1px solid ${T.borderSoft||T.border}`}}>
            <span>Bucket</span><span>Cards</span><span>% of Sales</span><span>Sell-Through</span><span>Total</span>
          </div>
          {bucketData.map((b,i) => (
            <div key={b.label} style={{display:"grid", gridTemplateColumns:"1.2fr 0.6fr 0.7fr 1fr 0.8fr", gap:8,
              padding:"10px 16px", fontSize:13, alignItems:"center",
              borderTop: i>0 ? `1px solid ${T.borderSoft||T.border}` : "none"}}>
              <span style={{display:"flex", alignItems:"center", gap:8}}>
                <span style={{width:10, height:10, borderRadius:3, background:BUCKET_COLORS[i % BUCKET_COLORS.length], flexShrink:0}} />
                {b.label}
              </span>
              <span>{b.count}</span>
              <span>{b.percent.toFixed(1)}%</span>
              <span style={{
                fontWeight:700,
                color: b.sellThrough == null ? T.textFaint
                  : b.sellThrough >= 60 ? "#4ade80"
                  : b.sellThrough >= 30 ? "#fbbf24" : "#f87171",
              }}>
                {b.sellThrough == null ? "—" : `${b.sellThrough.toFixed(0)}% (${b.count}/${b.count + b.onHand})`}
              </span>
              <span style={{fontWeight:700}}>{fmt(b.total)}</span>
            </div>
          ))}
        </div>
        </>
      )}

      <div style={S.card}>
        <div style={{fontSize:14, fontWeight:700, marginBottom:12}}>Custom Range</div>
        <div style={{display:"flex", gap:10, alignItems:"flex-end", marginBottom:16, flexWrap:"wrap"}}>
          <div>
            <label style={{fontSize:11, color:T.textFaint, display:"block", marginBottom:4}}>Min Price</label>
            <input style={S.input} type="number" min="0" step="0.01" placeholder="0.00"
              value={customMin} onChange={e=>setCustomMin(e.target.value)} />
          </div>
          <div>
            <label style={{fontSize:11, color:T.textFaint, display:"block", marginBottom:4}}>Max Price</label>
            <input style={S.input} type="number" min="0" step="0.01" placeholder="No limit"
              value={customMax} onChange={e=>setCustomMax(e.target.value)} />
          </div>
        </div>
        {customResult && (
          <div style={{display:"flex", gap:10, flexWrap:"wrap"}}>
            <StatCard T={T} icon="🃏" label="Cards Sold" value={customResult.count} />
            <StatCard T={T} icon="📊" label="% of Total Sales" value={`${customResult.percent.toFixed(1)}%`} />
            <StatCard T={T} icon="💰" label="Total Value" value={fmt(customResult.total)} color="#4ade80" />
          </div>
        )}
      </div>
    </div>
  );
}

function SalesTaxSummary({ inventory, S, T }) {
  const today = new Date().toISOString().slice(0,10);
  const ninetyAgo = new Date(Date.now() - 90*86400000).toISOString().slice(0,10);
  const [startDate, setStartDate] = useState(ninetyAgo);
  const [endDate, setEndDate]     = useState(today);

  const rows = useMemo(() => {
    const groups = {};
    inventory.forEach(i => {
      if (i.Status !== "Sold" || !i.SoldDate) return;
      if (i.SoldDate < startDate || i.SoldDate > endDate) return;
      const channel = saleChannelLabel(i);
      if (!channel) return; // Trades excluded
      const key = `${i.SoldDate}__${channel}`;
      if (!groups[key]) {
        groups[key] = { date:i.SoldDate, channel, taxableSales:0, taxRate:null, taxCollected:0, total:0 };
      }
      const price = parseFloat(i.SoldPrice) || 0;
      const tax   = parseFloat(i.TaxAmount) || 0;
      groups[key].taxableSales += price;
      groups[key].taxCollected += tax;
      groups[key].total        += price + tax;
      if (groups[key].taxRate == null && i.TaxRate != null) groups[key].taxRate = i.TaxRate;
    });
    return Object.values(groups).sort((a,b) => b.date.localeCompare(a.date));
  }, [inventory, startDate, endDate]);

  const totals = rows.reduce((acc,r) => ({
    taxableSales: acc.taxableSales + r.taxableSales,
    taxCollected: acc.taxCollected + r.taxCollected,
    total:        acc.total + r.total,
  }), { taxableSales:0, taxCollected:0, total:0 });

  return (
    <div>
      <SectionHeader T={T} title="🧾 Sales & Tax Summary" sub="Grouped by day and sales channel" />

      <div style={{display:"flex", gap:10, alignItems:"flex-end", marginBottom:16, flexWrap:"wrap"}}>
        <div>
          <label style={{fontSize:11, color:T.textFaint, display:"block", marginBottom:4}}>Start Date</label>
          <input style={S.input} type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} />
        </div>
        <div>
          <label style={{fontSize:11, color:T.textFaint, display:"block", marginBottom:4}}>End Date</label>
          <input style={S.input} type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} />
        </div>
      </div>

      <div style={{display:"flex", gap:10, flexWrap:"wrap", marginBottom:16}}>
        <StatCard T={T} icon="💵" label="Taxable Sales"  value={fmt(totals.taxableSales)} />
        <StatCard T={T} icon="🧾" label="Tax Collected"  value={fmt(totals.taxCollected)} color="#88bdf2" />
        <StatCard T={T} icon="💰" label="Total Collected" value={fmt(totals.total)} color="#4ade80" />
      </div>

      {rows.length === 0 ? (
        <div style={{textAlign:"center", padding:40, color:T.textMuted}}>
          No taxed sales in this date range.
        </div>
      ) : (
        <div style={{...S.card, padding:0, overflowX:"auto"}}>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1.3fr 1fr 0.7fr 1fr 1fr", gap:8, minWidth:640,
            padding:"10px 16px", fontSize:11, color:T.textFaint, textTransform:"uppercase",
            letterSpacing:"0.05em", borderBottom:`1px solid ${T.borderSoft||T.border}`}}>
            <span>Date</span><span>Show / Location</span><span>Taxable Sales</span>
            <span>Tax Rate</span><span>Tax Collected</span><span>Total Collected</span>
          </div>
          {rows.map((r,i) => (
            <div key={i} style={{display:"grid", gridTemplateColumns:"1fr 1.3fr 1fr 0.7fr 1fr 1fr", gap:8, minWidth:640,
              padding:"10px 16px", fontSize:13, alignItems:"center",
              borderTop: i>0 ? `1px solid ${T.borderSoft||T.border}` : "none"}}>
              <span>{r.date}</span>
              <span style={{color:T.textMuted}}>{r.channel}</span>
              <span>{fmt(r.taxableSales)}</span>
              <span>{r.taxRate != null ? `${r.taxRate}%` : "—"}</span>
              <span style={{color:"#88bdf2"}}>{fmt(r.taxCollected)}</span>
              <span style={{fontWeight:700, color:"#4ade80"}}>{fmt(r.total)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── P&L REPORT ───────────────────────────────────────────────────────────────
function PandL({ inventory, shows, lots, vendor, S, T }) {
  const [period, setPeriod] = useState("ytd"); // ytd | q1 | q2 | q3 | q4 | alltime | custom
  const [customStart, setCustomStart] = useState("");
  const [customEnd,   setCustomEnd]   = useState("");
  const [showDetail,  setShowDetail]  = useState(false);
  const [detailSort,  setDetailSort]  = useState("date_desc");

  // ── Date range from period ──────────────────────────────────────────────────
  const dateRange = useMemo(() => {
    const now   = new Date();
    const year  = now.getFullYear();
    if (period === "ytd")     return { start: new Date(`${year}-01-01`), end: now };
    if (period === "alltime") return { start: new Date("2000-01-01"),    end: now };
    if (period === "q1")      return { start: new Date(`${year}-01-01`), end: new Date(`${year}-03-31`) };
    if (period === "q2")      return { start: new Date(`${year}-04-01`), end: new Date(`${year}-06-30`) };
    if (period === "q3")      return { start: new Date(`${year}-07-01`), end: new Date(`${year}-09-30`) };
    if (period === "q4")      return { start: new Date(`${year}-10-01`), end: new Date(`${year}-12-31`) };
    if (period === "custom" && customStart && customEnd)
      return { start: new Date(customStart), end: new Date(customEnd) };
    return { start: new Date(`${year}-01-01`), end: now };
  }, [period, customStart, customEnd]);

  // ── Sold cards in period ───────────────────────────────────────────────────
  const soldInPeriod = useMemo(() => {
    return inventory.filter(i => {
      if (i.Status !== "Sold") return false;
      if (!i.SoldDate) return false;
      const d = new Date(i.SoldDate);
      return d >= dateRange.start && d <= dateRange.end;
    });
  }, [inventory, dateRange]);

  // ── Core P&L numbers ──────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const revenue  = soldInPeriod.reduce((s,i) => s + (parseFloat(i.SoldPrice)||0), 0);
    const cogs     = soldInPeriod.reduce((s,i) => s + (parseFloat(i.AcquisitionCost)||0), 0);
    const gross    = revenue - cogs;
    const margin   = revenue > 0 ? (gross / revenue) * 100 : 0;
    const losses   = soldInPeriod.filter(i =>
      (parseFloat(i.SoldPrice)||0) < (parseFloat(i.AcquisitionCost)||0)
    );
    const totalLoss = losses.reduce((s,i) =>
      s + ((parseFloat(i.AcquisitionCost)||0) - (parseFloat(i.SoldPrice)||0)), 0);
    const avgSalePrice = soldInPeriod.length > 0 ? revenue / soldInPeriod.length : 0;

    // Tax collected — informational only, deliberately NOT part of revenue/
    // gross/margin above. Sales tax isn't the vendor's money, it's held on
    // behalf of the tax authority, so it shouldn't inflate profit figures.
    const taxCollected = soldInPeriod.reduce((s,i) => s + (parseFloat(i.TaxAmount)||0), 0);

    // Unrealized: current inventory where market < cost
    const unrealized = inventory.filter(i =>
      (i.Status === "In Stock" || i.Status === "Reserved") &&
      i.MarketPrice > 0 && i.AcquisitionCost > 0 &&
      i.MarketPrice < i.AcquisitionCost
    );
    const unrealizedLoss = unrealized.reduce((s,i) =>
      s + ((parseFloat(i.AcquisitionCost)||0) - (parseFloat(i.MarketPrice)||0)), 0);

    // Inventory cost basis
    const inStock = inventory.filter(i => i.Status==="In Stock"||i.Status==="Reserved");
    const costBasis    = inStock.reduce((s,i) => s+(parseFloat(i.AcquisitionCost)||0), 0);
    const marketValue  = inStock.reduce((s,i) => s+(parseFloat(i.MarketPrice)||parseFloat(i.ListPrice)||0), 0);

    return {
      revenue, cogs, gross, margin, losses: losses.length, totalLoss,
      avgSalePrice, unrealizedLoss, unrealized: unrealized.length,
      costBasis, marketValue, count: soldInPeriod.length, taxCollected,
    };
  }, [soldInPeriod, inventory]);

  // ── By channel ────────────────────────────────────────────────────────────
  const byChannel = useMemo(() => {
    const map = {};
    soldInPeriod.forEach(i => {
      const ch = i.SoldChannel || "Unknown";
      if (!map[ch]) map[ch] = { channel:ch, revenue:0, cogs:0, count:0 };
      map[ch].revenue += parseFloat(i.SoldPrice)||0;
      map[ch].cogs    += parseFloat(i.AcquisitionCost)||0;
      map[ch].count++;
    });
    return Object.values(map).sort((a,b) => b.revenue - a.revenue);
  }, [soldInPeriod]);

  // ── By month ──────────────────────────────────────────────────────────────
  const byMonth = useMemo(() => {
    const map = {};
    soldInPeriod.forEach(i => {
      const d = new Date(i.SoldDate);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      const label = d.toLocaleString("default",{month:"short",year:"numeric"});
      if (!map[key]) map[key] = { key, label, revenue:0, cogs:0, count:0 };
      map[key].revenue += parseFloat(i.SoldPrice)||0;
      map[key].cogs    += parseFloat(i.AcquisitionCost)||0;
      map[key].count++;
    });
    return Object.values(map).sort((a,b) => a.key.localeCompare(b.key));
  }, [soldInPeriod]);

  // ── By show ───────────────────────────────────────────────────────────────
  const byShow = useMemo(() => {
    const map = {};
    soldInPeriod.filter(i => i.ShowName).forEach(i => {
      const key = i.ShowName;
      if (!map[key]) map[key] = { show:key, revenue:0, cogs:0, count:0 };
      map[key].revenue += parseFloat(i.SoldPrice)||0;
      map[key].cogs    += parseFloat(i.AcquisitionCost)||0;
      map[key].count++;
    });
    return Object.values(map).sort((a,b) => b.revenue - a.revenue);
  }, [soldInPeriod]);

  // ── Sorted detail rows ────────────────────────────────────────────────────
  const detailRows = useMemo(() => {
    const rows = [...soldInPeriod];
    if (detailSort === "date_desc") rows.sort((a,b) => new Date(b.SoldDate)-new Date(a.SoldDate));
    if (detailSort === "date_asc")  rows.sort((a,b) => new Date(a.SoldDate)-new Date(b.SoldDate));
    if (detailSort === "profit_desc") rows.sort((a,b) =>
      ((parseFloat(b.SoldPrice)||0)-(parseFloat(b.AcquisitionCost)||0)) -
      ((parseFloat(a.SoldPrice)||0)-(parseFloat(a.AcquisitionCost)||0)));
    if (detailSort === "revenue_desc") rows.sort((a,b) => (parseFloat(b.SoldPrice)||0)-(parseFloat(a.SoldPrice)||0));
    return rows;
  }, [soldInPeriod, detailSort]);

  // ── CSV Export ────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const headers = ["Date","Card Name","Set","Condition","Game","Channel","Sale Price","Cost","Profit","Margin%"];
    const rows = detailRows.map(i => {
      const sale   = parseFloat(i.SoldPrice)||0;
      const cost   = parseFloat(i.AcquisitionCost)||0;
      const profit = sale - cost;
      const margin = sale > 0 ? ((profit/sale)*100).toFixed(1) : "0.0";
      return [
        i.SoldDate||"", i.CardName||"", i.Set||"", i.Condition||"",
        i.GameType||"", i.SoldChannel||"",
        sale.toFixed(2), cost.toFixed(2), profit.toFixed(2), margin,
      ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(",");
    });
    const csv  = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `vendorstax-pl-${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const PERIODS = [
    { id:"ytd",     label:"Year to Date" },
    { id:"q1",      label:"Q1" },
    { id:"q2",      label:"Q2" },
    { id:"q3",      label:"Q3" },
    { id:"q4",      label:"Q4" },
    { id:"alltime", label:"All Time" },
    { id:"custom",  label:"Custom" },
  ];

  return (
    <div>
      {/* ── Period selector ─────────────────────────────────────────────── */}
      <div style={{display:"flex", gap:6, flexWrap:"wrap", marginBottom:20, alignItems:"center"}}>
        {PERIODS.map(p => (
          <button key={p.id}
            style={{...S.chip(period===p.id), padding:"7px 14px", fontSize:12}}
            onClick={()=>setPeriod(p.id)}>
            {p.label}
          </button>
        ))}
        <button onClick={exportCSV} style={{
          marginLeft:"auto", ...S.btn("ghost"),
          fontSize:12, padding:"7px 14px",
          display:"flex", alignItems:"center", gap:6,
        }}>
          ⬇ Export CSV
        </button>
      </div>

      {/* Custom date range */}
      {period === "custom" && (
        <div style={{display:"flex", gap:10, marginBottom:16, flexWrap:"wrap"}}>
          <div style={{flex:1, minWidth:140}}>
            <label style={S.label}>From</label>
            <input style={S.input} type="date" value={customStart}
              onChange={e=>setCustomStart(e.target.value)} />
          </div>
          <div style={{flex:1, minWidth:140}}>
            <label style={S.label}>To</label>
            <input style={S.input} type="date" value={customEnd}
              onChange={e=>setCustomEnd(e.target.value)} />
          </div>
        </div>
      )}

      {soldInPeriod.length === 0 ? (
        <div style={{...S.card, textAlign:"center", padding:40, color:T.textMuted}}>
          <div style={{fontSize:32, marginBottom:12}}>📊</div>
          <div style={{fontSize:15, fontWeight:600, marginBottom:8}}>No sales in this period</div>
          <div style={{fontSize:13}}>Try selecting a different date range.</div>
        </div>
      ) : (
        <>
          {/* ── Top summary cards ──────────────────────────────────────── */}
          <SectionHeader T={T} title="💰 Revenue & Profit" sub={`${stats.count} cards sold in this period`} />
          <div style={{display:"flex", gap:10, flexWrap:"wrap", marginBottom:10}}>
            <StatCard T={T} icon="💵" label="Total Revenue"   value={fmt(stats.revenue)} color="#4ade80" />
            <StatCard T={T} icon="📦" label="Cost of Goods"   value={fmt(stats.cogs)}    color="#f87171" />
            <StatCard T={T} icon="📈" label="Gross Profit"    value={fmt(stats.gross)}
              color={stats.gross >= 0 ? "#4ade80" : "#f87171"}
              sub={`${pct(stats.margin)} margin`} />
          </div>
          <div style={{display:"flex", gap:10, flexWrap:"wrap", marginBottom:4}}>
            <StatCard T={T} icon="🃏" label="Cards Sold"      value={stats.count} />
            <StatCard T={T} icon="💲" label="Avg Sale Price"  value={fmt(stats.avgSalePrice)} />
            <StatCard T={T} icon="📉" label="Cards Sold at a Loss" value={stats.losses}
              color={stats.losses > 0 ? "#f87171" : T.text}
              sub={stats.losses > 0 ? `${fmt(stats.totalLoss)} total loss` : "None — great job!"} />
          </div>

          {vendor?.tax_tracking_enabled && stats.taxCollected > 0 && (
            <div style={{display:"flex", gap:10, flexWrap:"wrap", marginBottom:4}}>
              <StatCard T={T} icon="🧾" label="Tax Collected" value={fmt(stats.taxCollected)}
                color="#88bdf2" sub="Not counted as revenue — held for the tax authority" />
            </div>
          )}

          {/* ── Monthly trend chart ────────────────────────────────────── */}
          {byMonth.length > 1 && (
            <>
              <SectionHeader T={T} title="📅 Monthly Trend" />
              <div style={{...S.card, padding:"16px 8px"}}>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={byMonth} margin={{top:0,right:8,bottom:0,left:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                    <XAxis dataKey="label" tick={{fontSize:10,fill:T.textMuted}} />
                    <YAxis tick={{fontSize:10,fill:T.textMuted}}
                      tickFormatter={v=>`$${Math.round(v)}`} width={48} />
                    <Tooltip
                      contentStyle={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,fontSize:12}}
                      formatter={(v,name) => [`$${v.toFixed(2)}`, name==="revenue"?"Revenue":"COGS"]}
                    />
                    <Bar dataKey="revenue" fill="#3C66EE" radius={[4,4,0,0]} name="revenue" />
                    <Bar dataKey="cogs"    fill="#f87171" radius={[4,4,0,0]} name="cogs" />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{display:"flex", gap:16, justifyContent:"center", marginTop:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:T.textMuted}}>
                    <div style={{width:10,height:10,borderRadius:2,background:"#3C66EE"}}/>Revenue
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:T.textMuted}}>
                    <div style={{width:10,height:10,borderRadius:2,background:"#f87171"}}/>COGS
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── By channel ────────────────────────────────────────────── */}
          {byChannel.length > 0 && (
            <>
              <SectionHeader T={T} title="📡 Sales by Channel" />
              <div style={S.card}>
                {byChannel.map(ch => {
                  const profit = ch.revenue - ch.cogs;
                  const margin = ch.revenue > 0 ? (profit/ch.revenue*100) : 0;
                  return (
                    <div key={ch.channel} style={{
                      display:"flex", justifyContent:"space-between", alignItems:"center",
                      padding:"10px 0", borderBottom:`0.5px solid ${T.border}`,
                    }}>
                      <div>
                        <div style={{fontSize:13, fontWeight:600}}>{ch.channel}</div>
                        <div style={{fontSize:11, color:T.textMuted}}>{ch.count} cards</div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:13, fontWeight:700, color:"#4ade80"}}>{fmt(ch.revenue)}</div>
                        <div style={{fontSize:11, color: profit>=0 ? "#4ade80" : "#f87171"}}>
                          {profit>=0?"+ ":"− "}{fmt(Math.abs(profit))} ({pct(margin)})
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── By show ───────────────────────────────────────────────── */}
          {byShow.length > 0 && (
            <>
              <SectionHeader T={T} title="🎪 Sales by Show" />
              <div style={S.card}>
                {byShow.map(sh => {
                  const profit = sh.revenue - sh.cogs;
                  const margin = sh.revenue > 0 ? (profit/sh.revenue*100) : 0;
                  return (
                    <div key={sh.show} style={{
                      display:"flex", justifyContent:"space-between", alignItems:"center",
                      padding:"10px 0", borderBottom:`0.5px solid ${T.border}`,
                    }}>
                      <div>
                        <div style={{fontSize:13, fontWeight:600}}>{sh.show}</div>
                        <div style={{fontSize:11, color:T.textMuted}}>{sh.count} cards sold</div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:13, fontWeight:700, color:"#4ade80"}}>{fmt(sh.revenue)}</div>
                        <div style={{fontSize:11, color: profit>=0 ? "#4ade80" : "#f87171"}}>
                          {profit>=0?"+ ":"− "}{fmt(Math.abs(profit))} ({pct(margin)})
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Inventory valuation ───────────────────────────────────── */}
          <SectionHeader T={T} title="📦 Current Inventory Snapshot"
            sub="What your unsold inventory is worth right now" />
          <div style={{display:"flex", gap:10, flexWrap:"wrap", marginBottom:4}}>
            <StatCard T={T} icon="💰" label="Total Cost Basis"  value={fmt(stats.costBasis)}
              sub="What you paid for unsold cards" />
            <StatCard T={T} icon="📊" label="Est. Market Value" value={fmt(stats.marketValue)}
              sub="Based on market/list prices" />
            <StatCard T={T} icon={stats.marketValue >= stats.costBasis ? "📈" : "📉"}
              label="Unrealized Gain/Loss"
              value={`${stats.marketValue >= stats.costBasis ? "+" : "−"}${fmt(Math.abs(stats.marketValue - stats.costBasis))}`}
              color={stats.marketValue >= stats.costBasis ? "#4ade80" : "#f87171"}
              sub={stats.unrealized > 0 ? `${stats.unrealized} cards below cost` : "All cards at or above cost"} />
          </div>

          {/* ── Sales detail table ────────────────────────────────────── */}
          <div style={{marginTop:28, marginBottom:12, display:"flex",
            justifyContent:"space-between", alignItems:"center"}}>
            <div>
              <div style={{fontSize:14, fontWeight:700}}>🧾 Sales Detail</div>
              <div style={{fontSize:12, color:T.textMuted, marginTop:2}}>
                Every card sold in this period — export as CSV for your accountant
              </div>
            </div>
            <button
              onClick={()=>setShowDetail(v=>!v)}
              style={{...S.btn("ghost"), fontSize:12, padding:"7px 14px"}}>
              {showDetail ? "Hide ↑" : `Show All ${stats.count} →`}
            </button>
          </div>

          {showDetail && (
            <div style={S.card}>
              <div style={{display:"flex", gap:8, marginBottom:12, flexWrap:"wrap"}}>
                <label style={{fontSize:11,color:T.textMuted,alignSelf:"center"}}>Sort by:</label>
                {[
                  ["date_desc","Newest First"],
                  ["date_asc","Oldest First"],
                  ["profit_desc","Most Profit"],
                  ["revenue_desc","Highest Sale"],
                ].map(([id,label])=>(
                  <button key={id} style={{...S.chip(detailSort===id), fontSize:11, padding:"5px 10px"}}
                    onClick={()=>setDetailSort(id)}>{label}</button>
                ))}
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
                  <thead>
                    <tr>
                      {["Date","Card","Game","Channel","Sale Price","Cost","Profit"].map(h=>(
                        <th key={h} style={{
                          padding:"8px 10px", textAlign:"left",
                          borderBottom:`1px solid ${T.border}`,
                          color:T.textMuted, fontWeight:600, fontSize:11,
                          textTransform:"uppercase", letterSpacing:"0.04em",
                          whiteSpace:"nowrap",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map((item,i) => {
                      const sale   = parseFloat(item.SoldPrice)||0;
                      const cost   = parseFloat(item.AcquisitionCost)||0;
                      const profit = sale - cost;
                      return (
                        <tr key={item.ID||i} style={{
                          background: i%2===0 ? "transparent" : `${T.surface2}`,
                        }}>
                          <td style={{padding:"8px 10px", color:T.textMuted, whiteSpace:"nowrap"}}>
                            {item.SoldDate || "—"}
                          </td>
                          <td style={{padding:"8px 10px"}}>
                            <div style={{fontWeight:500}}>{item.CardName}</div>
                            <div style={{fontSize:10, color:T.textFaint}}>{item.Set} · {item.Condition}</div>
                          </td>
                          <td style={{padding:"8px 10px", color:T.textMuted}}>{item.GameType||"—"}</td>
                          <td style={{padding:"8px 10px", color:T.textMuted}}>{item.SoldChannel||"—"}</td>
                          <td style={{padding:"8px 10px", fontWeight:600, color:"#4ade80"}}>{fmt(sale)}</td>
                          <td style={{padding:"8px 10px", color:T.textMuted}}>{fmt(cost)}</td>
                          <td style={{padding:"8px 10px", fontWeight:700,
                            color: profit>=0 ? "#4ade80" : "#f87171"}}>
                            {profit>=0?"+":""}{fmt(profit)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{borderTop:`2px solid ${T.border}`}}>
                      <td colSpan={4} style={{padding:"10px", fontWeight:700, fontSize:12}}>
                        TOTAL ({stats.count} cards)
                      </td>
                      <td style={{padding:"10px", fontWeight:700, color:"#4ade80"}}>{fmt(stats.revenue)}</td>
                      <td style={{padding:"10px", fontWeight:700, color:"#f87171"}}>{fmt(stats.cogs)}</td>
                      <td style={{padding:"10px", fontWeight:700,
                        color: stats.gross>=0 ? "#4ade80" : "#f87171"}}>
                        {stats.gross>=0?"+":""}{fmt(stats.gross)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
